import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { filterNilValue } from '@datorama/akita';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NGXLogger } from 'ngx-logger';
import {
  EMPTY,
  Observable,
  catchError,
  combineLatest,
  debounceTime,
  defer,
  distinctUntilChanged,
  finalize,
  first,
  firstValueFrom,
  forkJoin,
  from,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { v4 as uuid } from 'uuid';

import { assert, assertExists, assertTypeByKey } from 'common';
import {
  Archive,
  ArchiveImportFile,
  BLACK,
  DXCSource,
  DocumentAnnotations,
  DocumentAttachment,
  DocumentProvider,
  DocumentUpdateProvider,
  Field,
  FieldValues,
  ImportedArchiveDocument,
  InboxFile,
  InboxSession,
  PdfOptions,
  Search,
  SearchOptions,
  SearchPrompt,
  SearchResult,
  SearchResults,
  TextLayer,
  TextLayers,
  UserFriendlyError,
  YELLOW_HIGHLIGHT,
  createApiSearchPromptString,
  createInboxSessionDocumentFromInboxFile,
  filterReadOnlyPermissions,
} from 'models';
import {
  CURRENT_DOCUMENT_REVISION,
  INTERNAL_VIEWER_CLIENT_SUPPORTED_FILE_TYPES,
} from 'src/app/common/constants';
import {
  DOCUMENT_PROVIDER,
  DOCUMENT_UPDATE_PROVIDER,
} from 'src/app/common/tokens';
import {
  ActionsMenu,
  AnnotationsMenu,
  ArchiveImportRequestedDocument,
  ArchiveImportRequestedDocumentMap,
  ArchiveRequestedDocument,
  ArchiveRequestedDocumentMap,
  ArchiveRevisionRequestedDocument,
  ArchiveRevisionRequestedDocumentMap,
  ArchiveSearchRequestedDocument,
  ArchiveSearchRequestedDocumentMap,
  DirtyComponent,
  DocumentUpdateSession,
  InboxDocumentOpenRequest,
  InboxRequestedDocument,
  InboxRequestedDocumentMap,
  SearchResultDocumentOpenRequest,
  SupportedRequestedDocumentMap,
  assertSupportedAnnotation,
} from 'src/app/models';
import {
  ConvertableFileType,
  DocumentEmailDataLoadedEvent,
  PdfModifiedEvent,
  PdfViewerComponent,
} from 'src/app/modules/pdf-viewer';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { DocumentAnnotationService } from 'src/app/services/document-annotation.service';
import { LayoutService } from 'src/app/services/layout.service';
import { NotificationService } from 'src/app/services/notification.service';
import { SearchUIService } from 'src/app/services/search-ui.service';
import { TableFieldUIService } from 'src/app/services/table-field-ui.service';
import { ViewerService } from 'src/app/services/viewer.service';
import { ApplicationQuery } from 'src/app/state/application/application.query';
import { ApplicationService } from 'src/app/state/application/application.service';
import { ArchiveSessionCacheQuery } from 'src/app/state/archive-document-cache/archive-document-cache.query';
import { ArchiveDocumentCacheService } from 'src/app/state/archive-document-cache/archive-document.cache.service';
import { ArchivesQuery } from 'src/app/state/archives/archives.query';
import { ArchivesService } from 'src/app/state/archives/archives.service';
import { DatabasesQuery } from 'src/app/state/databases/databases.query';
import { InboxesQuery } from 'src/app/state/inboxes/inboxes.query';
import { InboxesService } from 'src/app/state/inboxes/inboxes.service';
import { SearchesQuery } from 'src/app/state/searches/searches.query';
import { SearchesService } from 'src/app/state/searches/searches.service';
import { TaskSearchesService } from 'src/app/state/task-searches/task-searches.service';

import { toSignal } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { createDataUrl, getFileExtension } from 'src/app/common/utility';
import { CommandService } from 'src/app/modules/command-palette';
import { PdfThumbnail } from 'src/app/modules/pdf-viewer/models';
import { EmailAttachmentsService } from 'src/app/services/email-attachments.service';
import { NewPdfDocumentService } from 'src/app/services/new-documents.service';
import { SearchResultCacheService } from 'src/app/services/search-result-cache.service';
import { SupportedFeaturesService } from 'src/app/services/supported-features.service';
import { UiService } from 'src/app/services/ui.service';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../confirmation-dialog/confirmation-dialog.component';
import { DocumentViewSidebarComponent } from '../document-view-sidebar/document-view-sidebar.component';
import { IndexerField } from '../indexer/indexer.component';
import { RelatedSearchSelectedEvent } from '../related-searches-menu/related-searches-menu.component';
import { UserActionExecutedEvent } from '../user-actions-panel/user-actions-panel.component';

/** Document View. */
@UntilDestroy()
@Component({
  selector: 'app-document-view',
  templateUrl: './document-view.component.html',
  styleUrls: ['./document-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DocumentViewComponent
  implements OnInit, DirtyComponent, OnDestroy
{
  /** Actions menu. */
  @ViewChild('actionMenuComponent')
  actionMenuComponent: ActionsMenu;
  /** PDF Viewer reference. */
  @ViewChild(PdfViewerComponent)
  pdfviewer: PdfViewerComponent;
  /** Annotations menu. */
  @ViewChild('annotationsMenuComponent')
  annotationsMenuComponent: AnnotationsMenu;
  /** Document view sidebar reference. */
  @ViewChild('rightSidebar')
  rightSidebar: DocumentViewSidebarComponent;

  /** Annotation Canvas. */
  annotationCanvas: HTMLCanvasElement;
  /** Annotation Context. */
  annotationContext: CanvasRenderingContext2D;
  /** Archive Id. */
  archiveId: number;
  /** Canvas Element. */
  canvas: HTMLCanvasElement;
  /** Currently selected field. */
  currentField: Field;
  /** Current hovered text layer. */
  currentTextLayer: TextLayer;
  /** Database Id. */
  databaseId: number;
  /** @inheritdoc */
  dirtyFn?: (() => Observable<boolean>) | undefined;
  /**
   * Observable document result.
   *
   * Watches changes in routing to update the loaded document.
   */
  document$ = combineLatest([
    this.searchesQuery.searchRouteParams$,
    this.routerQuery.selectParams([
      'inboxId',
      'documentId',
      'viewIndex',
      'taskId',
      'sessionId',
    ]),
    this.routerQuery.selectData('isImport'),
    this.routerQuery.selectQueryParams('targetArchiveId'),
  ]).pipe(
    untilDestroyed(this),
    debounceTime(1),
    tap(() => {
      // Reset state.
      this.isUnsupportedDocument = false;
      this.logger.info('Updating loading document based on routing.');
    }),
    switchMap(
      ([
        [databaseId, archiveId, searchId, searchPrompts],
        [inboxId, documentId, viewIndex, taskId, sessionId],
        isImport,
        targetArchiveId,
      ]) => {
        this.documentDirty = false;
        this.forceImportToUsePdfBytes = false;
        this.showModifyDocumentThumbnailer = false;
        this.isImport = !!isImport;
        this.documentAttachments.set([]);
        this.importFieldValues.set([]);
        this.logger.debug('Import mode: ', this.isImport);
        // All require.
        assertExists(databaseId, 'Database Id is required.');

        // Document id here could be undefined if using an archive session.
        // That code path already updates the requested documents itself.
        if (documentId) {
          // Requested documents is set in ngOnInit on initial load of this component but does not update it when the route changes.
          // We need to ensure that the value is always updated.
          this.requestedDocuments =
            this.archiveId > 0 && !this.isImport
              ? documentId
                  .split(',')
                  .map((d: Number | string) =>
                    isNaN(Number(d)) ? d : Number(d)
                  )
              : documentId.split(',');
        }

        if (typeof inboxId !== 'undefined') {
          // Inboxes.
          if (targetArchiveId) {
            this.inboxTargetArchiveId = Number(targetArchiveId);
            this.importUniqueId = uuid();
            this.tableFieldUIService.clear();
            this.listenToTableFieldEvent();
          }
          return this.getSearchResultForInboxRoute(
            databaseId,
            inboxId,
            documentId,
            viewIndex
          );
        } else if (typeof searchId !== 'undefined') {
          // Archives.
          assertExists(archiveId, 'Archive Id is required.');
          // TODO Is this the right place to put this?
          this.listenToTableFieldEvent();
          return this.getSearchResultForArchiveRoute(
            archiveId,
            databaseId,
            searchId,
            searchPrompts,
            documentId,
            viewIndex
          );
        } else if (typeof taskId !== 'undefined') {
          assertExists(archiveId, 'Archive Id is required.');
          this.listenToTableFieldEvent();
          return this.getSearchResultForTaskSearchRoute(
            databaseId,
            archiveId,
            documentId,
            viewIndex
          );
        } else if (this.isImport) {
          this.importUniqueId = uuid();
          this.listenToTableFieldEvent();
          return this.getArchiveImportFile(viewIndex, documentId);
        } else if (
          typeof archiveId !== 'undefined' &&
          typeof sessionId !== 'undefined'
        ) {
          this.logger.debug('Checking if archive view mode can be used.');
          this.listenToTableFieldEvent();
          return this.getSearchResultFromArchiveSession(
            sessionId,
            viewIndex,
            databaseId,
            archiveId
          );
        } else if (typeof archiveId !== 'undefined') {
          if (ArchiveRevisionRequestedDocument.looksLike(documentId)) {
            this.viewingRevision = true;
            this.listenToTableFieldEvent();
            return this.getSearchResultForArchiveRevision(
              databaseId,
              archiveId,
              documentId
            );
          } else if (ArchiveRequestedDocument.looksLike(documentId)) {
            return this.getSearchResultFromArchiveDocument(
              databaseId,
              archiveId,
              documentId
            );
          } else {
            throw new TypeError(
              'Archive requested document route not yet supported.'
            );
          }
        } else {
          throw new TypeError('Inbox Id or Search Id are required.');
        }
      }
    ),
    catchError(() => this.handleMissingDocument()),
    filterNilValue(),
    shareReplay(1)
  );
  documentAttachments = signal<DocumentAttachment[]>([]);
  /** Whether the document is dirty. */
  documentDirty = false;
  /** Active drag. */
  dragActive = false;
  /** Drag text. */
  dragText = '';
  /** Whether importing a document should use the bytes from the pdf viewer. */
  forceImportToUsePdfBytes = false;
  /** Force OCR. */
  forceOcr = false;
  /** Should KFI use Auto OCR. */
  autoOcr = true;
  /** Observable of if there are any annotations. */
  hasAnnotations$ = this.annotationService.annotations$.pipe(
    untilDestroyed(this),
    map((annotations) => annotations.length > 0)
  );
  /** Unique Id for imports. */
  importUniqueId = '';
  /** Any field data to be populated in the indexer for imports. */
  importFieldValues = signal<FieldValues>([]);
  /** Archive Id where inbox document should be indexed. */
  inboxTargetArchiveId = 0;
  /** Observable of whether the indexer sidebar should be open. */
  indexerSidebarOpen$ = this.auth.isGuest
    ? of(false) // Never show the sidebar if the user is a guest.
    : this.appQuery.indexerSidebarOpen$;
  /** If the indexer should reload after saving */
  indexerReloadAfterSave: boolean;
  /** Is the document currently loading. */
  isDocumentLoading = false;
  /** If the user is guest. */
  isGuest = this.auth.isGuest;
  /** Whether the viewer is in import mode. */
  isImport = false;
  /** Is mouse down. */
  isMouseDown = false;
  /** If the user should be restricted to one document. */
  isRestrictedUser = this.auth.isRestrictedUser;
  /** Document search is loading. */
  isSearchLoading = true;
  /** Is shift keydown. */
  isShiftDown = false;
  /** If the document format is unsupported. */
  isUnsupportedDocument = false;
  /** Is KFI Active. */
  kfiActive = false;
  /** Track the kfi hover text. */
  kfiHoverText = '';
  /** Object for tracking kfi rectangle. */
  kfiRectangle = {
    x: 0,
    y: 0,
    startX: 0,
    startY: 0,
  };
  /** Rectangle div element. */
  kfiRectangleDiv: HTMLDivElement;
  /** Track the kfi hover text when shift is held. */
  kfiShiftHoverText = '';
  /** Last focused table field. */
  lastFocusedTableField = 0;
  /** Last page line focused. */
  lastLine = 0;
  /** Current page number. */
  pageNumber = 1;
  /** Is modify document supported using the connected API. */
  modifyDocumentsSupported = this.supportedFeatures.versionSupports(
    this.supportedFeatures.modifyDocuments
  );
  /** Observable number of new PDF documents in the database. */
  newPdfDocumentCount$ = this.databasesQuery.activeDbId$.pipe(
    untilDestroyed(this),
    switchMap((databaseId) => {
      if (!databaseId) return of(0); // No need to try getting count before database is active.
      return this.newDocumentService.observeCountInDatabase$(databaseId);
    }),
    shareReplay(1)
  );
  /** Observable of whether new PDF documents exist on the database */
  newPdfDocumentsExist$ = this.newPdfDocumentCount$.pipe(
    map((count) => count > 0)
  );
  /** PDF options. */
  pdfOptions?: PdfOptions;
  /** Track redraw interval. */
  redrawInterval: ReturnType<typeof setInterval>;
  /** Regular Expression highlight canvas. */
  regexHighlightCanvas: HTMLCanvasElement;
  /** Regular Expression highlight context. */
  regexHighlightContext: CanvasRenderingContext2D;
  /** Version control archive. */
  revisionArchive: Archive;
  /** Whether a save is in progress. */
  saveInProgress = false;
  /** Whether to show the modify document thumbnailer. */
  showModifyDocumentThumbnailer = false;
  /** Observable determining whether to show the archive revisions menu. */
  showRevisionsMenu$ = this.archivesQuery.active$.pipe(
    map(
      (archive) =>
        this.displayMode !== 'inbox' &&
        this.displayMode !== 'inboxIndexing' &&
        this.displayMode !== 'archiveImport' &&
        (archive?.isVersioning ?? false)
    )
  );
  /** Whether to show the table field grid. */
  showTableFieldGrid = false;
  tableFieldGrid = computed(() => this.ui.state().tableField);
  /** Table Field Tracking. */
  tableFieldIds: number[];
  /** Text Layers. */
  textLayers: TextLayers = [];
  /** Thumbnails. */
  thumbnails: PdfThumbnail[] = [];
  /** If a compact layout should be used. */
  useCompactLayout = false;
  /** Use local ocr. */
  useLocalOcr = false;
  /** Is a revision being viewed. */
  viewingRevision = false;
  /**
   * Observable of queue metadata for the document.
   *
   * This can be undefined if the document is not a search result, is not in a workflow/queue, or if there is failure attemping to run the api call.
   */
  workflowQueuedDocument = toSignal(
    this.document$.pipe(
      switchMap((document) => {
        try {
          assertTypeByKey<SearchResult>(
            document,
            'secureId',
            'string',
            'This method requires the document be a SearchResult.'
          );
          if (document.workflowProcessId === 0) return of(undefined); // The document is not in a workflow so it cannot be in a queue.
          return this.taskSearchesService.api
            .getQueuedDocumentMetadata(
              this.databaseId,
              this.archiveId,
              document.id,
              document.secureId
            )
            .pipe(
              catchError((error) => {
                // This is here to make absolutely sure the switchMap doesn't stop listening for new documents if an error is thrown here.
                this.logger.warn(
                  'Unable to retrieve queue metadata for the document. This may be because the document is not in a queue.',
                  error
                );
                return of(undefined);
              })
            );
        } catch {
          // Catch here is for assertion errors meaning the document is not a search result and so none of the above logic applies to it.
          return of(undefined);
        }
      })
    )
  );
  /** Are annotations dirty. */
  isAnnotationDirty = false;

  private archiveCacheSessionId: string;
  private autoSave = false;
  private document: SearchResult | InboxFile | ArchiveImportFile;
  private inboxId: number;
  /** Store a list of document Id or filenames (archive or inbox). */
  private requestedDocuments: SupportedRequestedDocumentMap;
  private searchId: number;
  private sessionId: number;
  private taskId: string;
  private viewIndex = 0;
  /** Returns if annotations are supported. */
  private annotationsSupported: boolean;

  constructor(
    private searchesQuery: SearchesQuery,
    private searchesService: SearchesService,
    private searchUIService: SearchUIService,
    private searchResultCacheService: SearchResultCacheService,
    private annotationService: DocumentAnnotationService,
    private inboxesService: InboxesService,
    private inboxesQuery: InboxesQuery,
    private routerQuery: RouterQuery,
    private archiveCacheQuery: ArchiveSessionCacheQuery,
    private archivesQuery: ArchivesQuery,
    private logger: NGXLogger,
    @Inject(DOCUMENT_PROVIDER) private documentProvider: DocumentProvider,
    @Inject(DOCUMENT_UPDATE_PROVIDER)
    private documentUpdateProvider: DocumentUpdateProvider,
    private auth: AuthenticationService,
    private appQuery: ApplicationQuery,
    private databasesQuery: DatabasesQuery,
    private application: ApplicationService,
    private viewerService: ViewerService,
    private archiveService: ArchivesService,
    private archiveCacheService: ArchiveDocumentCacheService,
    private newDocumentService: NewPdfDocumentService,
    private router: Router,
    private layout: LayoutService,
    private notify: NotificationService,
    private tableFieldUIService: TableFieldUIService,
    private ui: UiService,
    private emailAttachmentsService: EmailAttachmentsService,
    private breakpointObserver: BreakpointObserver,
    private changeDetectorReference: ChangeDetectorRef,
    private taskSearchesService: TaskSearchesService,
    private commandService: CommandService,
    private supportedFeatures: SupportedFeaturesService,
    private dialog: MatDialog,
    cdr: ChangeDetectorRef
  ) {
    this.annotationService.dirty$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged() // Only emit if the value changes
      )
      .subscribe((dirty) => {
        this.isAnnotationDirty = dirty;
        cdr.markForCheck();
      });

    this.annotationService.annotationChanged$
      .pipe(untilDestroyed(this), debounceTime(1000))
      .subscribe(() => {
        if (this.shouldAutoSave && this.annotationService.dirty$.getValue()) {
          this.autoSaveAnnotationChanges();
        }
      });
  }

  /**
   * Returns the active revision number for the current document.
   *
   * @return {number} The active revision number. Returns 0 if the display mode is not 'archiveRevision'.
   */
  get activeRevisionNumber(): number {
    if (this.displayMode !== 'archiveRevision') {
      return 0;
    }

    return (
      this.requestedDocuments.get(
        this.viewIndex
      ) as ArchiveRevisionRequestedDocument
    ).versionNumber;
  }

  /** Creates annotations */
  onClickCreateHighlightAnnotation(event: MouseEvent) {
    this.annotationService.enableDrawRectangle(event, YELLOW_HIGHLIGHT, true);
  }

  /** Creates annotations */
  onClickCreateRedactionAnnotation(event: MouseEvent) {
    this.annotationService.enableDrawRectangle(event, BLACK, false);
  }

  /** Creates annotations */
  onClickCreateTextAnnotation(event: MouseEvent) {
    this.annotationService.enableDrawNote(event);
  }

  onClickCreateStampAnnotation(event: MouseEvent, text: string) {
    this.annotationService.enableDrawStamp(event, text);
  }
  /**
   * Should the action menu button be disabled.
   *
   * @returns True if the action menu button should be disabled.
   */
  get disableActionMenuButton(): boolean {
    return this.kfiActive || this.displayMode === 'archiveSession';
  }

  /**
   * Gets whether the save button should be disabled.
   *
   * @returns True if the save button should be disabled.
   */
  get disableSave(): boolean {
    return (
      this.saveInProgress ||
      (this.rightSidebar?.indexer?.disableSave &&
        !this.tableFieldGrid()?.isDirty &&
        !this.isAnnotationDirty &&
        !this.documentDirty)
    );
  }

  /**
   * Get the current display mode based on loaded state.
   *
   * @returns Display mode string.
   */
  get displayMode():
    | 'archive'
    | 'archiveSession'
    | 'inbox'
    | 'inboxIndexing'
    | 'task'
    | 'archiveImport'
    | 'archiveRevision' {
    if (this.taskId) return 'task';
    if (this.isImport) return 'archiveImport';
    if (this.sessionId) return 'archiveSession';
    if (typeof this.inboxId !== 'undefined' && !Number.isNaN(this.inboxId)) {
      return this.inboxTargetArchiveId > 0 ? 'inboxIndexing' : 'inbox';
    }
    if (typeof this.archiveId !== 'undefined')
      return this.viewingRevision ? 'archiveRevision' : 'archive';
    throw new Error(
      'Loaded state does not meet criteria for any handled display mode.'
    );
  }

  /**
   * Get the document as ArchiveImportFile.
   *
   * @returns ArchiveImportFile.
   */
  get documentAsArchiveImportFile(): ArchiveImportFile {
    assertTypeByKey<ArchiveImportFile>(
      this.document,
      'filename',
      'string',
      'This method requires the document to be an ArchiveImportFile.'
    );

    return this.document;
  }

  /**
   * Get the document as InboxFile.
   *
   * @returns InboxFile.
   */
  get documentAsInboxFile(): InboxFile {
    assertTypeByKey<InboxFile>(
      this.document,
      'filename',
      'string',
      'This method requires the document be an InboxFile.'
    );
    return this.document;
  }

  /**
   * Get the document as SearchResult.
   *
   * @returns SearchResult.
   * @throws {AssertionError} if the document is not a SearchResult.
   */
  get documentAsSearchResult(): SearchResult {
    assertTypeByKey<SearchResult>(
      this.document,
      'secureId',
      'string',
      'This method requires the document be a SearchResult.'
    );
    return this.document;
  }

  /**
   * Get the document filetype
   *
   * @returns The filetype as a string.
   */
  get documentFileType(): string {
    switch (this.displayMode) {
      case 'archive':
      case 'archiveSession':
      case 'task':
      case 'archiveRevision':
        return this.documentAsSearchResult.fileType;
      case 'inbox':
      case 'inboxIndexing':
        return this.documentAsInboxFile.fileType;
      case 'archiveImport':
        // Grab the extension from the filename and return it in the same format (with the prepended dot) as the others.
        const filenameFragments =
          this.documentAsArchiveImportFile.filename.split('.');
        const sanitizedFileExtension =
          filenameFragments[filenameFragments.length - 1];
        return `.${sanitizedFileExtension}`;
    }
  }

  /**
   * Get the current and total document numbers.
   *
   * @returns Object with properties for current and total number.
   */
  get documentOfDocuments() {
    if (typeof this.viewIndex !== 'number' || !this.requestedDocuments?.size) {
      // Short circuit to undefined if required values for view are not available.
      return;
    }
    return {
      current: this.viewIndex + 1,
      total: this.requestedDocuments.size,
    };
  }

  /**
   * Gets the document's permissions.
   *
   * @returns A permissions object.
   */
  get documentPermissions() {
    switch (this.displayMode) {
      case 'archive':
      case 'archiveSession':
      case 'task':
        return this.documentAsSearchResult.permissions;
      case 'archiveImport':
        return this.archivesQuery.active.permissions;
      case 'archiveRevision':
        if (this.activeRevisionNumber === CURRENT_DOCUMENT_REVISION) {
          return this.documentAsSearchResult.permissions;
        }
        return filterReadOnlyPermissions(
          this.documentAsSearchResult.permissions
        );
      case 'inbox':
        return this.inboxesQuery.active.permissions;
      case 'inboxIndexing':
        return this.archivesQuery.getArchive(this.inboxTargetArchiveId)
          .permissions;
      default:
        throw new Error('Unsupported display mode.');
    }
  }

  get excludedClientSideFormats(): ConvertableFileType[] {
    const excludedFormats: ConvertableFileType[] = [];
    try {
      if (this.displayMode !== 'archiveImport') {
        excludedFormats.push('msg', 'eml');
      }
    } catch {
      // This could through if displayMode isn't ready yet.
    }

    return excludedFormats;
  }

  /** @inheritdoc */
  get isDirty(): boolean {
    const tableFieldGrid = this.tableFieldGrid();
    return (
      this.documentDirty ||
      this.isAnnotationDirty ||
      (typeof this.rightSidebar !== 'undefined' && this.rightSidebar.isDirty) ||
      (typeof tableFieldGrid !== 'undefined' && tableFieldGrid.isDirty)
    );
  }

  /**
   * Is the user disallowed from downloading the document.
   *
   * @returns True when download is not allowed.
   */
  get isDownloadDisabled() {
    // todo this method of preventing type errors might need some work.
    if (!this.document.hasOwnProperty('permissions')) {
      return true;
    }
    assertTypeByKey<SearchResult>(
      this.document,
      'secureId',
      'string',
      'This method requires the document be a SearchResult.'
    );
    return !this.document.permissions.exportDocuments;
  }

  /**
   * Is the user disallowed from using KFI.
   *
   * @returns True if the user can use KFI.
   */
  get isKfiDisabled(): boolean {
    return (
      this.isDocumentLoading ||
      !this.appQuery.indexerSidebarOpen ||
      !this.documentPermissions.modifyData
    );
  }

  /**
   * Is there more than one document open?
   *
   * @returns True if there are more than one document requested for open.
   */
  get isMultipleDocuments() {
    return !!this.requestedDocuments && this.requestedDocuments.size > 1;
  }

  /**
   * Is a next open document available.
   *
   * @returns True if there is a higher index document available.
   */
  get isNextAvailable() {
    return (
      typeof this.viewIndex === 'number' &&
      !!this.requestedDocuments &&
      this.viewIndex <= this.requestedDocuments.size - 2
    );
  }

  /**
   * Is a previous open document available.
   *
   * @returns True if there is a lower index document available.
   */
  get isPreviousAvailable() {
    return typeof this.viewIndex === 'number' && this.viewIndex > 0;
  }

  /**
   * Determines if the modify button should be disabled.
   *
   * @returns True if the modify button should be disabled.
   */
  get modifyDocumentDisabled(): boolean {
    const fileExtension =
      this.displayMode === 'archiveImport'
        ? getFileExtension(this.documentAsArchiveImportFile.filename)
        : this.documentAsSearchResult.fileType.toLowerCase();

    return (
      !this.documentPermissions.modifyDocuments ||
      fileExtension === '.tif' ||
      fileExtension === '.tiff'
    );
  }

  /**
   * Is there more than one document open?
   *
   * @returns True if there are more than one document open.
   */
  get multipleDocumentsOpen(): boolean {
    return this.requestedDocuments.size > 1;
  }

  /**
   * Parent document ID for a revision controlled document.
   *
   * This is the document ID of the version of the document that exists
   * in the version controlled archive **not** a revision in the `Versions` archive.
   *
   * @returns The document secure ID.
   */
  get revisionParentDocumentId() {
    if (
      this.displayMode === 'archive' ||
      this.displayMode === 'archiveSession' ||
      this.displayMode === 'task'
    ) {
      // We are looking at a document that isn't a previous revision so return the document id from the url.
      return this.requestedDocuments.get(
        this.viewIndex
      ) as ArchiveSearchRequestedDocument;
    } else if (this.displayMode === 'archiveRevision') {
      // We are looking at a previous version of the document so we need to get the id from the requested document object.
      return (
        this.requestedDocuments.get(
          this.viewIndex
        ) as ArchiveRevisionRequestedDocument
      ).docId;
    } else {
      throw new Error(
        'Getting the revision parent secure id is not supported for this display mode.'
      );
    }
  }

  /**
   * Parent document secure ID for a revision controlled document.
   *
   * This is the secure ID of the version of the document that exists
   * in the version controlled archive **not** a revision in the `Versions` archive.
   *
   * @returns The document secure ID.
   */
  get revisionParentSecureId() {
    if (
      this.displayMode === 'archive' ||
      this.displayMode === 'archiveSession' ||
      this.displayMode === 'task'
    ) {
      // We are looking at a document that isn't a previous revision so just return the secure id.
      return this.documentAsSearchResult.secureId;
    } else if (this.displayMode === 'archiveRevision') {
      // We are looking at a previous version of the document so we need to get the secure id from the requested document object.
      return (
        this.requestedDocuments.get(
          this.viewIndex
        ) as ArchiveRevisionRequestedDocument
      ).secureId;
    } else {
      throw new Error(
        'Getting the revision parent secure id is not supported for this display mode.'
      );
    }
  }

  /** @ineritdoc */
  get shouldAutoSave(): boolean {
    return (
      (this.displayMode === 'archive' ||
        this.displayMode === 'task' ||
        this.displayMode === 'archiveSession') &&
      this.archivesQuery.getShouldAutoSave(this.archiveId)
    );
  }

  /**
   * Should the locked document menu be shown.
   *
   * @returns True if the locked document menu should be shown.
   */
  get shouldShowLockedDocumentMenu(): boolean {
    return (
      this.displayMode === 'archive' ||
      this.displayMode === 'task' ||
      this.displayMode === 'archiveSession'
    );
  }

  /**
   * Should modify thumbenailer be shown.
   *
   * @returns True if the modify thumbnailer should be shown.
   */
  get shouldShowModifyThumbnailer(): boolean {
    return (
      this.showModifyDocumentThumbnailer &&
      this.documentAsSearchResult.fileType !== '.tiff' &&
      this.documentAsSearchResult.fileType !== '.tif' &&
      this.pdfviewer &&
      !this.isDocumentLoading
    );
  }

  /**
   * Should the navigation toggle be shown.
   *
   * @returns If the control should be shown.
   */
  get shouldShowNavigationToggle() {
    return !this.isGuest && !this.useCompactLayout && !this.isRestrictedUser;
  }

  /**
   * Should the annotations menu be displayed.
   *
   * @returns True if the annotations menu should be displayed.
   */
  get showAnnotationsMenu(): boolean {
    return (
      this.displayMode !== 'inbox' &&
      this.displayMode !== 'inboxIndexing' &&
      this.documentPermissions.modifyAnnotations
    );
  }

  /**
   * Should PDF preview be shown during import.
   *
   * @returns True if the preview should be shown.
   */
  get showArchiveImportPreview(): boolean {
    assert(
      this.displayMode === 'archiveImport',
      'The display mode must be ArchiveImport.'
    );

    return INTERNAL_VIEWER_CLIENT_SUPPORTED_FILE_TYPES.some((extension) =>
      (
        this.requestedDocuments.get(
          this.viewIndex
        ) as ArchiveImportRequestedDocument
      )
        .toLowerCase()
        .endsWith(extension)
    );
  }

  /**
   * Determines if the attachments menu should be displayed.
   *
   * @returns True if the attachments menu should be displayed.
   */
  get showAttachmentsMenu(): boolean {
    return !this.isDocumentLoading && this.documentAttachments().length > 0;
  }

  /**
   * Determines if the save PDF converted file button should be displayed.
   *
   * @returns True if the save PDF converted file button should be displayed.
   */
  get showConvertToPdfButton(): boolean {
    if (
      !this.documentPermissions.modifyDocuments ||
      this.displayMode === 'inbox' ||
      this.displayMode === 'inboxIndexing'
    ) {
      return false;
    }
    const filetype = this.documentFileType.toLowerCase();
    const documentFragments = filetype.split('.');
    const sanitizedFileExtension = documentFragments[
      documentFragments.length - 1
    ]
      .replace('.', '')
      .toLowerCase();
    return PdfViewerComponent.isClientConvertableExtension(
      sanitizedFileExtension,
      ['eml', 'msg'] // don't allow email files since its an easy way to lose your attachments.
    );
  }

  /**
   * Should the DXC menu component load.
   *
   * The component itself handles hiding if there are no sources.
   *
   * @returns True if the DXC menu should load.
   */
  get showDXCMenu() {
    return (
      this.displayMode === 'archive' ||
      this.displayMode === 'archiveSession' ||
      this.displayMode === 'archiveImport' ||
      this.displayMode === 'task' ||
      this.displayMode === 'inboxIndexing'
    );
  }

  /**
   * Determines if the modify document toggle button should be displayed.
   *
   * @returns True if the modify document toggle button should be displayed.
   */
  get showModifyDocumentToggle(): boolean {
    if (
      this.displayMode === 'inbox' ||
      this.displayMode === 'inboxIndexing' ||
      this.displayMode === 'archiveImport'
    ) {
      return false;
    }
    return this.modifyDocumentsSupported;
  }

  /**
   * Should the save button be displayed.
   *
   * @returns True if the save button should be displayed.
   */
  get showSave(): boolean {
    return this.displayMode !== 'inbox';
  }

  /**
   * Should the save all button be displayed.
   *
   * @returns True if the save all button should be displayed.
   */
  get showSaveAll(): boolean {
    return this.displayMode === 'archiveImport' && this.isMultipleDocuments;
  }

  /**
   * Should the toggle indexer button be displayed.
   *
   * @returns True if the toggle indexer button should be displayed.
   */
  get showToggleIndexer(): boolean {
    return this.displayMode !== 'inbox';
  }

  /**
   * Style attached to the viewer container element.
   *
   * Height will adjust for the presence of table field controls.
   *
   * @returns CSS rule for th esize of the viewer container.
   */
  get viewerContainerStyle() {
    // Base heights for different configurations
    const baseHeights = {
      default: '100vh',
      compactTableGrid: {
        compact: 'calc(100vh - 136px)',
        regular: 'calc(100vh - 264px)',
      },
      regularTableGrid: {
        compact: 'calc(50vh + 40px)',
        regular: 'calc(50vh + 12px)',
      },
    };

    // Toolbar height adjustments
    const toolbarHeights = {
      wide: 128,
      narrow: 120,
    };

    // Determine if we're in compact mode
    const isCompactMode = this.appQuery.useCompactMode;

    // Determine toolbar height based on screen size
    const isFullWidthView =
      this.breakpointObserver.isMatched('(min-width: 600px)');
    let toolbarHeight = isFullWidthView
      ? toolbarHeights.wide
      : toolbarHeights.narrow;

    // Adjust toolbar height for compact mode
    if (isCompactMode) {
      toolbarHeight -= 56;
    }

    // Select base height based on configuration
    let baseHeight;
    if (!this.showTableFieldGrid) {
      baseHeight = baseHeights.default;
    } else if (this.appQuery.getUseCompactTableFieldGrid()) {
      baseHeight = isCompactMode
        ? baseHeights.compactTableGrid.compact
        : baseHeights.compactTableGrid.regular;
    } else {
      baseHeight = isCompactMode
        ? baseHeights.regularTableGrid.compact
        : baseHeights.regularTableGrid.regular;
    }

    return {
      height: `calc(${baseHeight} - ${toolbarHeight}px)`,
    };
  }

  /**
   * Handler for the new document dialog click event.
   */
  onClickOpenNewDocumentsDialog(): void {
    this.newDocumentService.openNewDocumentDialog();
  }

  /**
   * Handles marking a converted document for save.
   */
  onClickMarkDocumentConversionForSave(): void {
    if (
      this.pdfviewer.partialLoadState &&
      this.pdfviewer.partialLoadState.loaded <
        this.pdfviewer.partialLoadState.total
    ) {
      // The viewer hasn't loaded all the pages yet so load them.
      this.logger.debug(
        'Viewer is displaying a partial tiff. Loading the complete document.'
      );
      this.pdfviewer.onClickLoadCompleteDocument();
    }

    this.documentDirty = true;
    if (
      this.displayMode === 'archiveImport' ||
      this.displayMode === 'inboxIndexing'
    ) {
      this.logger.debug('Setting import to use the PDF viewer bytes.');
      this.forceImportToUsePdfBytes = true;
    }
    this.notify.info('DOCUMENT_CONVERSION_MARKED_FOR_SAVE_MSG');
  }

  ngOnDestroy(): void {
    if (this.kfiActive) {
      this.onKfiToggle();
    }

    // Unregister component from the UI.
    this.ui.unregister(this);

    // Unregister command pallete handlers.
    this.commandService.unregister('Save', 'Document');

    this.annotationService.destroyAnnotations();
  }

  ngOnInit(): void {
    this.listenForUserSettings();
    // Collect the requested document routing information.
    this.routerQuery
      .selectParams([
        'dbId',
        'archiveId',
        'searchId',
        'inboxId',
        'taskId',
        'documentId',
        'sessionId',
        'viewIndex',
      ])
      .pipe(
        untilDestroyed(this),
        first(),
        map(
          ([
            databaseId,
            archiveId,
            searchId,
            inboxId,
            taskId,
            documentId,
            sessionId,
            viewIndex,
          ]) => {
            this.databaseId = Number(databaseId);
            this.archiveId = Number(archiveId);
            this.searchId = Number(searchId);
            this.inboxId = Number(inboxId);
            this.taskId = taskId;
            this.sessionId = sessionId;
            // TODO decide if this needs to be observable instead
            this.isImport = this.routerQuery.getData('isImport') ?? false;

            // Reset user action overrides when a new document is opened.
            this.application.clearUserActionButtonOverrides();

            if (typeof sessionId !== 'undefined') {
              // get the initial set of documents
              const requestedDocuments = this.archiveCacheQuery
                .getSession(sessionId)
                .documents.map((d) => d.id);
              this.requestedDocuments = new ArchiveSearchRequestedDocumentMap(
                requestedDocuments
              );
            } else {
              this.requestedDocuments =
                this.archiveId > 0 && !this.isImport
                  ? documentId
                      .split(',')
                      .map((d: Number | string) =>
                        isNaN(Number(d)) ? d : Number(d)
                      )
                  : documentId.split(',');
            }

            this.viewIndex = Number(viewIndex ?? 0);
          }
        )
      )
      .subscribe();
    // Subscribe to compact view state.
    this.layout.useCompactLayout$
      .pipe(untilDestroyed(this))
      .subscribe((useCompactLayout) => {
        this.useCompactLayout = useCompactLayout;
      });

    this.listenForAutoSave();

    // Register as active UI component.
    this.ui.register(this);

    // Register command pallete handlers.
    this.commandService.register(
      'Save',
      'Save the currently viewed document.',
      () => {
        if (!this.disableSave) {
          this.onClickSave();
        }
      },
      'Document'
    );

    this.annotationsSupported = this.supportedFeatures.versionSupports(
      this.supportedFeatures.annotationsDrawing
    );
  }

  /** Handler for the PDF viewer after print event. */
  onAfterPrint(): void {
    this.annotationService.reloadAnnotations(this.pdfviewer);
    this.changeDetectorReference.detectChanges();
  }

  /**
   * Handler for click of open actions menu.
   *
   * @param $event Event.
   */
  onClickActionMenu($event: any) {
    this.actionMenuComponent.openMenu($event, [this.document as any]);
  }

  onClickAnnotationsMenu($event: any) {
    this.annotationsMenuComponent.openMenu($event, [this.document as any]);
  }

  /** Handler for click of back button. */
  onClickBack() {
    assert(
      this.isPreviousAvailable,
      'Requested view index can not be less than 0.'
    );
    if (this.autoSave) {
      this.autoSave$().subscribe((success) => {
        if (!success) {
          return;
        }

        if (
          this.displayMode === 'archiveImport' ||
          this.displayMode === 'inboxIndexing'
        ) {
          // remove the document that was just imported
          this.requestedDocuments.delete(this.viewIndex);
        } else {
          // otherwise increment the index to move to the next doc
          this.viewIndex--;
        }

        this.navigateToDocument().subscribe((navigationSuccessful) => {
          if (!navigationSuccessful) {
            this.viewIndex--;
          }
        });
      });
    } else {
      this.viewIndex--;
      this.navigateToDocument().subscribe((navigationSuccessful) => {
        if (!navigationSuccessful) {
          this.viewIndex++;
        }
      });
    }
  }

  /**
   * Handler for the cancel indexing button click event.
   */
  onClickCancelIndexing(): void {
    if (this.displayMode !== 'inboxIndexing') {
      this.logger.error('Cancel indexing can only be used on inbox indexing.');
      return;
    }
    this.logger.debug('Cancel indexing clicked.');
    this.tableFieldUIService.clear();
    from(
      this.router.navigate(
        [
          'db',
          this.databaseId,
          'inbox',
          this.inboxId,
          'document',
          this.requestedDocuments.toString(),
          'view',
          this.viewIndex,
        ],
        {
          queryParams: { targetArchiveId: undefined },
          queryParamsHandling: 'merge',
        }
      )
    ).subscribe((routedSuccessfully) => {
      if (routedSuccessfully) {
        this.inboxTargetArchiveId = 0;
      }
    });
  }

  /** Handler for click of download. */
  onClickDownloadDocument() {
    assertTypeByKey<SearchResult>(
      this.document,
      'secureId',
      'string',
      'This action requires the document be a SearchResult.'
    );
    const downloadFileUrl = this.documentProvider.getArchiveDownloadUrl(
      this.databaseId,
      this.archiveId,
      this.document.id,
      this.auth.user.token,
      this.document.secureId
    );
    window.open(downloadFileUrl, '_blank');
  }

  /** Handler for click of forward button. */
  onClickForward() {
    assert(
      this.isNextAvailable,
      'Requested view index exceeds the size of the list of open documents.'
    );
    if (this.autoSave) {
      this.autoSave$().subscribe((success) => {
        if (!success) {
          return;
        }
        if (
          this.displayMode === 'archiveImport' ||
          this.displayMode === 'inboxIndexing'
        ) {
          // remove the document that was just imported
          this.requestedDocuments.delete(this.viewIndex);
        } else {
          // otherwise increment the index to move to the next doc
          this.viewIndex++;
        }
        this.navigateToDocument().subscribe((navigationSuccessful) => {
          if (!navigationSuccessful) {
            this.viewIndex--;
          }
        });
      });
    } else {
      this.viewIndex++;
      this.navigateToDocument().subscribe((navigationSuccessful) => {
        if (!navigationSuccessful) {
          this.viewIndex--;
        }
      });
    }
  }

  /** Handler for click return to search. */
  onClickReturnToSearch() {
    const navigate = (): Observable<boolean> => {
      return this.routerQuery
        .selectParams(['dbId', 'archiveId', 'searchId', 'inboxId', 'taskId'])
        .pipe(
          first(),
          switchMap(([databaseId, archiveId, searchId, inboxId, taskId]) => {
            if (taskId) {
              return from(
                this.router.navigate(
                  ['db', databaseId, 'archive', archiveId, 'task', taskId],
                  {
                    queryParamsHandling: 'merge',
                  }
                )
              );
            } else if (inboxId) {
              // Return to inbox.
              return from(
                this.router.navigate(['db', databaseId, 'inbox', inboxId], {
                  queryParams: {
                    targetArchiveId: undefined, // ensure param is removed to avoid odd behavior on opening another document
                  },
                  queryParamsHandling: 'merge',
                })
              );
            } else if (
              this.displayMode === 'archiveImport' ||
              this.displayMode === 'archiveSession' ||
              this.displayMode === 'archiveRevision'
            ) {
              return from(
                this.router.navigate(['db', databaseId, 'archive', archiveId], {
                  queryParamsHandling: 'merge',
                })
              );
            } else {
              if (this.appQuery.internalViewerClearCacheOnReturnToSearch) {
                // Clear the search cache
                this.searchResultCacheService.deleteCacheResult(
                  databaseId,
                  archiveId,
                  searchId
                );
              }
              // Return to search.
              const searchPrompts = this.searchesQuery.currentSearchPrompts;
              return this.searchUIService.redirectToSearch$(
                databaseId,
                archiveId,
                searchId,
                searchPrompts,
                'merge'
              );
            }
          })
        );
    };
    if (this.autoSave) {
      this.autoSave$().subscribe((success) => {
        if (!success) {
          return;
        }

        navigate().subscribe({
          next: (navigationSuccessful) => {
            if (navigationSuccessful) {
              this.tableFieldUIService.clear();
            }
          },
        });
      });
    } else {
      if (!this.tableFieldGrid()?.isDirty) {
        this.tableFieldUIService.clear();
      }
      navigate().subscribe({
        next: (navigationSuccessful) => {
          if (navigationSuccessful) {
            this.tableFieldUIService.clear();
          }
        },
      });
    }
  }

  /**
   * Handler for the save button click event.
   */
  onClickSave(): void {
    if (this.displayMode === 'inbox') {
      this.logger.error('Saving inbox document changes are not yet supported.');
      return;
    }

    if (this.rightSidebar.indexer.indexerForm.invalid) {
      this.logger.warn('Indexer form has errors.');
      return;
    }

    // We're dealing with an archive document if we get here.
    this.saveInProgress = true;

    const save$ = this.createSave$().pipe(
      finalize(() => {
        this.saveInProgress = false;
      })
    );
    save$.subscribe({
      next: ({ documentId, documentSecureId }) => {
        // this.notify.success('Field data updated successfully.');

        if (this.displayMode === 'inboxIndexing') {
          // Re-fetch inbox to update files in state cache.
          this.inboxesService
            .getById(this.inboxId)
            .pipe(first())
            .subscribe(() => {
              this.notify.success('INBOX_INDEX_SUCCESSFUL');
              const filenamesToRemove = [
                this.requestedDocuments.get(
                  this.viewIndex
                ) as InboxRequestedDocument,
              ];
              this.viewIndex = Math.min(
                this.viewIndex,
                this.requestedDocuments.size - 2
              );
              this.onRefreshSearch({
                filenames: filenamesToRemove,
              });
            });

          return;
        } else if (this.displayMode === 'archiveImport') {
          this.notify.success('DOCUMENT_IMPORTED_SUCCESSFULLY');
          const isNewSession = !this.archiveCacheSessionId;

          if (isNewSession) this.archiveCacheSessionId = uuid();
          this.archiveCacheService.createOrUpdateSession(
            this.archiveCacheSessionId,
            [
              {
                id: documentId,
                secureId: documentSecureId,
                archiveId: this.archiveId,
              },
            ]
          );

          const openDocuments = [...this.requestedDocuments.values()];
          openDocuments.splice(
            openDocuments.indexOf(this.documentAsArchiveImportFile.filename),
            1
          );

          if (isNewSession) {
            const urlCommands = [
              'db',
              this.databaseId,
              'archive',
              this.archiveId,
              'document',
              'session',
              this.archiveCacheSessionId,
            ];

            if (openDocuments.length === 0) {
              /* There was only one document being imported if this is a new session and there aren't more documents after removing this one.
               We'll instead navigate you to the session in this tab instead of opening another and return since none of the other logic here makes sense to run
               when the tab will be reloaded any way.
              */
              this.router.navigate(urlCommands);
              return;
            }
            const url = this.router.serializeUrl(
              this.router.createUrlTree(urlCommands)
            );

            window.open(url, '_blank');
          }

          // Go back if there are no more documents.
          if (openDocuments.length === 0) {
            this.onClickReturnToSearch();
            return;
          }

          this.router.navigate(
            [
              'db',
              this.databaseId,
              'archive',
              this.archiveId,
              'import',
              // Send multiple document results together, archive and document paired by `.` and each seperated by `,`.
              openDocuments.join(),
            ],
            { queryParamsHandling: 'merge' }
          );

          //Clear the annotation service after import.
          this.annotationService.clearAnnotationData();
        } else {
          this.notify.success('DOCUMENT_CHANGES_SAVED');
        }

        // Reload the index field values (rerunning livefields) if the user is configured for it.
        if (this.indexerReloadAfterSave) {
          this.logger.debug(
            'Save completed, user settings request a reload of indexer fields.'
          );
          this.reloadDocumentData().subscribe(() => {
            this.changeDetectorReference.detectChanges();
            this.rightSidebar.indexer.reloadIndexerFields();
          });
        }
      },
      error: (error: UserFriendlyError) => {
        this.notify.error(error);
      },
    });
  }

  /** Handler for the save all button click event. */
  onClickSaveAll(): void {
    if (this.displayMode !== 'archiveImport') {
      this.logger.error('Save all is only available for archive imports.');
      return;
    }

    if (this.rightSidebar.indexer.indexerForm.invalid) {
      this.logger.warn('Indexer form has errors.');
      return;
    }

    this.logger.debug('Imporing all documents.', this.requestedDocuments);
    this.saveInProgress = true;

    const imports$ = (
      [...this.requestedDocuments.values()] as ArchiveImportRequestedDocument[]
    ).map((filename) => {
      assert(typeof filename === 'string', 'Filename must be a string.');
      const updateSession = new DocumentUpdateSession(
        this.isAnnotationDirty,
        false,
        this.rightSidebar?.indexer?.isDirty ?? false,
        this.tableFieldGrid()?.isDirty ?? false
      );
      const import$ =
        this.documentAsArchiveImportFile.filename === filename &&
        this.forceImportToUsePdfBytes
          ? this.archiveService.api
              .importBytes(
                this.databaseId,
                this.archiveId,
                this.pdfviewer.pdfBytes,
                '.pdf',
                'application/pdf',
                updateSession
              )
              .pipe(
                tap(() => {
                  this.documentDirty = false;
                })
              )
          : this.archiveService.api
              .import(
                this.databaseId,
                this.archiveId,
                [filename],
                updateSession
              )
              .pipe(map((importDocuments) => importDocuments[0]));
      return import$.pipe(
        switchMap((importedDocument) => {
          assertExists(
            importedDocument,
            'Document object with id and secure id must be provided to save.'
          );

          const saveChain$ = this.createSaveChain(
            importedDocument.id,
            importedDocument.secureId,
            updateSession
          );

          return saveChain$.pipe(
            switchMap(() =>
              of({
                documentId: importedDocument.id,
                documentSecureId: importedDocument.secureId,
              })
            )
          );
        })
      );
    });

    combineLatest(imports$)
      .pipe(finalize(() => (this.saveInProgress = false)))
      .subscribe({
        next: (documents) => {
          this.notify.success('DOCUMENT_IMPORTED_SUCCESSFULLY');
          const archiveCacheSessionId = uuid();

          this.archiveCacheService.createOrUpdateSession(
            archiveCacheSessionId,
            documents.map((document) => ({
              id: document.documentId,
              secureId: document.documentSecureId,
              archiveId: this.archiveId,
            }))
          );
          this.router.navigate(
            [
              'db',
              this.databaseId,
              'archive',
              this.archiveId,
              'document',
              'session',
              archiveCacheSessionId,
            ],
            {
              replaceUrl: true,
            }
          );
        },
        error: (error: UserFriendlyError) => {
          this.notify.error(error);
        },
      });
  }

  /** Handler for click of toggle indexer. */
  onClickToggleIndexer() {
    this.application.toggleIndexerSidebarOpen();
    if (this.kfiActive) {
      this.onKfiToggle();
      this.changeDetectorReference.detectChanges();
    }
  }

  /** Handler for the close document button click event. */
  onCloseDocument(): void {
    this.logger.debug('Closing document.');
    if (this.requestedDocuments.size === 1) {
      this.logger.debug(
        'The active document is the last one in the session. Running return to search.'
      );
      this.onClickReturnToSearch();
      return;
    }
    // Create a clone of requested documents to manipulate and later use it to route.
    const requestedDocuments = this.requestedDocuments.clone();
    requestedDocuments.delete(this.viewIndex);
    const newViewIndex = Math.min(this.viewIndex, requestedDocuments.size - 1);

    /** Observable route commands or undefined if routing should not occur. */
    // biome-ignore lint/suspicious/noExplicitAny: The route commands accepted by router are of type any[].
    let routeCommands$: Observable<any[] | undefined> = of(undefined);

    switch (this.displayMode) {
      case 'archive':
      case 'task':
      case 'archiveSession':
      case 'archiveImport':
      case 'inboxIndexing':
        const autosaveSuccess$ = this.autoSave ? this.autoSave$() : of(true);
        routeCommands$ = autosaveSuccess$.pipe(
          map((autoSaveSuccess) => {
            if (!autoSaveSuccess) {
              this.logger.error(
                'Auto save failed. Document will not be closed.'
              );
              return undefined;
            }

            if (this.displayMode === 'archiveImport') {
              return [
                'db',
                this.databaseId,
                'archive',
                this.archiveId,
                'import',
                // Send multiple document results together, archive and document paired by `.` and each seperated by `,`.
                requestedDocuments.toString(),
                'view',
                newViewIndex,
              ];
            } else if (this.displayMode === 'inboxIndexing') {
              return [
                'db',
                this.databaseId,
                'inbox',
                this.inboxId,
                'document',
                requestedDocuments.toString(),
                'view',
                newViewIndex,
              ];
            } else if (this.displayMode === 'task') {
              return [
                'db',
                this.databaseId,
                'archive',
                this.archiveId,
                'task',
                this.taskId,
                'document',
                requestedDocuments.toString(),
                'view',
                newViewIndex,
              ];
            } else {
              return [
                'db',
                this.databaseId,
                'archive',
                this.archiveId,
                'search',
                this.searchId,
                'document',
                // Send multiple document results together, archive and document paired by `.` and each seperated by `,`.
                requestedDocuments.toString(),
                'view',
                newViewIndex,
              ];
            }
          })
        );
        break;
      case 'inbox':
        routeCommands$ = of([
          'db',
          this.databaseId,
          'inbox',
          this.inboxId,
          'document',
          // Send multiple document results together, archive and document paired by `.` and each seperated by `,`.
          requestedDocuments.toString(),
          'view',
          newViewIndex,
        ]);
        break;
      case 'archiveRevision':
        routeCommands$ = of(undefined);
        throw new Error(
          'Archive revisions cannot be closed with the close document button.'
        );
    }

    routeCommands$.subscribe((routeCommands) => {
      if (!routeCommands) {
        this.logger.error('Failed to get new route.');
        return;
      }
      this.router.navigate(routeCommands, { queryParamsHandling: 'merge' });
    });
  }

  /**
   * Handler for the on DXC source selected event.
   *
   * @param source DXC Source.
   */
  onDXCMatchSelected(source: DXCSource): void {
    assertExists(this.rightSidebar, 'Right sidebar must exist.');
    assertExists(this.rightSidebar.indexer, 'Indexer must exist.');
    this.rightSidebar.indexer.runDataXChange(source);
  }

  onDocumentEmailDataLoaded(event: DocumentEmailDataLoadedEvent): void {
    this.logger.debug('Email data loaded: ', event);
    const { emailData } = event;
    this.documentAttachments.set(event.emailData.attachments);
    if (!this.isImport) {
      this.logger.debug(
        'Document is not being imported. Skipping email field population.'
      );
      return;
    }

    const emailFieldNames = [
      'date:',
      'subject:',
      'priority:',
      'from:',
      'from address:',
      'to:',
      'cc:',
      'attachments:',
    ];
    const archiveEmailFields = this.archivesQuery
      .getFields(this.archiveId)
      .filter((f) => emailFieldNames.includes(f.name.toLowerCase()));

    const emailFieldValues: FieldValues = [];

    for (const field of archiveEmailFields) {
      switch (field.name.toLowerCase()) {
        case 'date:':
          if (emailData.date) {
            const isoDate = moment(emailData.date).toISOString();
            emailFieldValues.push({
              id: field.id,
              value: field.multiValue ? '' : isoDate,
              multiValue: field.multiValue ? [isoDate] : [],
            });
          }
          break;
        case 'subject:':
          if (emailData.subject) {
            emailFieldValues.push({
              id: field.id,
              value: field.multiValue ? '' : emailData.subject,
              multiValue: field.multiValue ? [emailData.subject] : [],
            });
          }
          break;
        case 'priority:':
          if (emailData.priority) {
            emailFieldValues.push({
              id: field.id,
              value: field.multiValue ? '' : emailData.priority,
              multiValue: field.multiValue ? [emailData.priority] : [],
            });
          }
          break;
        case 'from:':
          if (emailData.sender && emailData.sender.name) {
            emailFieldValues.push({
              id: field.id,
              value: field.multiValue ? '' : emailData.sender.name,
              multiValue: field.multiValue ? [emailData.sender.name] : [],
            });
          }
          break;
        case 'from address:':
          if (emailData.sender && emailData.sender.email) {
            emailFieldValues.push({
              id: field.id,
              value: field.multiValue ? '' : emailData.sender.email,
              multiValue: field.multiValue ? [emailData.sender.email] : [],
            });
          }
          break;
        case 'to:':
          if (emailData.recipients) {
            const recipients: string[] = [];
            for (const recipient of emailData.recipients) {
              const recipientString = recipient.toString();
              if (recipientString) {
                recipients.push(recipientString);
              }
            }
            emailFieldValues.push({
              id: field.id,
              value: field.multiValue ? '' : recipients.join('; '),
              multiValue: field.multiValue ? recipients : [],
            });
          }
          break;
        case 'cc:':
          if (emailData.cc) {
            const recipients: string[] = [];
            for (const recipient of emailData.cc) {
              const recipientString = recipient.toString();
              if (recipientString) {
                recipients.push(recipientString);
              }
            }

            emailFieldValues.push({
              id: field.id,
              value: field.multiValue ? '' : recipients.join('; '),
              multiValue: field.multiValue ? recipients : [],
            });
          }
          break;
        case 'attachments:':
          if (emailData.attachments.length > 0) {
            const attachmentFilenames = emailData.attachments.map(
              (a) => a.name
            );
            emailFieldValues.push({
              id: field.id,
              value: field.multiValue ? '' : attachmentFilenames.join('; '),
              multiValue: field.multiValue ? attachmentFilenames : [],
            });
          }
          break;
      }
    }

    this.importFieldValues.set(emailFieldValues);
  }

  /**
   * Handler for the document loading change event.
   *
   * @param documentIsLoading Whether the document is currently loading.
   */
  onDocumentLoadingChange(documentIsLoading: boolean) {
    this.logger.debug(
      'PDF thumbnailer open: ',
      this.showModifyDocumentThumbnailer
    );
    if (this.isDocumentLoading && !documentIsLoading) {
      const thumbnailContainer = document.querySelector(
        '.thumbnailer-viewer-container'
      );
      this.annotationService.initializeService(
        this.pdfviewer,
        thumbnailContainer,
        this.pageNumber,
        this.documentPermissions.modifyAnnotations,
        this.documentPermissions.modifyDocuments,
        this.saveInProgress
      );
    }

    if (
      this.displayMode === 'archive' &&
      this.documentAsSearchResult.contentSearch.hits > 0 &&
      this.isDocumentLoading &&
      !documentIsLoading
    ) {
      this.logger.debug(
        'Document is no longer loading and content search terms are present.'
      );
      this.pdfviewer.findInDocument(
        this.documentAsSearchResult.contentSearch.terms[0], // TODO: do we support multiple terms in the s9 viewer somehow?
        false,
        true,
        true
      );
    }

    if (
      this.isDocumentLoading &&
      !documentIsLoading &&
      this.displayMode !== 'inbox' &&
      this.displayMode !== 'archiveRevision' &&
      !this.kfiActive &&
      this.appQuery.keyfreeAutoStartOnDocumentLoad &&
      this.documentPermissions.modifyData
    ) {
      // Automatically enable keyfree when a document finishes loading if keyfree is not already active, a document with editable index data
      // is being displayed, and the setting is enabled.
      this.onKfiToggle();
    }

    this.isDocumentLoading = documentIsLoading;

    // Trigger change detection so that KFI button is correctly re-enabled.
    this.changeDetectorReference.detectChanges();
  }

  /** Handler for the indexer's Data XChange match applied event. */
  onIndexerDataXChangeMatchApplied(): void {
    if (this.shouldAutoSave) {
      if (!this.documentPermissions.modifyData) {
        // Make absolutely certain that the user has permissions to modify data.
        this.logger.debug(
          'User does not have modify data permissions. Index data will not be saved automatically.'
        );
        return;
      }

      const documentSearchResult = this.documentAsSearchResult;
      const updateSession = new DocumentUpdateSession(
        false,
        false,
        true,
        false
      );
      this.createSaveIndexData$(
        this.archiveId,
        documentSearchResult.id,
        documentSearchResult.secureId,
        updateSession
      ).subscribe({
        next: () => {
          this.notify.success('FIELD_DATA_SAVED');
          this.rightSidebar.indexer.reloadLiveFields();
        },
        error: (error: UserFriendlyError) => {
          this.notify.error(error);
        },
      });
    }
  }

  /**
   * Handler for the indexer field focused event.
   *
   * @param field Field that was focused.
   */
  onIndexerFieldFocused(field: Field) {
    // Stop grid editting if there is currently a table field.
    this.tableFieldGrid()?.grid.api.stopEditing();
  }

  /**
   * Handler for the indexer field blurred event.
   *
   * @param field Field that was blurred.
   */
  onIndexerFieldBlurred(field: Field) {
    if (this.shouldAutoSave) {
      this.autoSaveIndexData(field);
    }
  }

  /**
   * Handler for the indexer multi-value field entry removed event.
   *
   * @param field Field that had a value removed.
   */
  onIndexerFieldMultiValueEntryRemoved(field: Field) {
    if (this.shouldAutoSave) {
      this.autoSaveIndexData(field);
    }
  }

  /**
   * Keyfree indexing on click.
   *
   * @param forceOcr - Force toggle on ocr.
   */
  onKfiToggle(forceOcr?: boolean): void {
    this.kfiActive = !this.kfiActive;
    this.forceOcr = forceOcr ?? this.forceOcr;
  }

  /** Hander for failure to load document. */
  onLoadFailure() {
    this.notify.warning('DOCUMENT_NOT_FOUND_SHOW_SEARCH_RESULTS');
    // Remove the current document and refresh.
    const restrictViewIndex = () => {
      this.viewIndex = Math.min(
        this.viewIndex,
        this.requestedDocuments.size - 2
      );
    };
    if (this.displayMode === 'archive') {
      const idsToRemove = [
        this.requestedDocuments.get(
          this.viewIndex
        ) as ArchiveSearchRequestedDocument,
      ];
      restrictViewIndex();
      this.onRefreshSearch({
        ids: idsToRemove,
      });
    } else {
      const filenamesToRemove = [
        this.requestedDocuments.get(this.viewIndex) as InboxRequestedDocument,
      ];
      restrictViewIndex();
      this.onRefreshSearch({
        filenames: filenamesToRemove,
      });
    }
    // todo we will need to handle when the requestedDocuments.documents is a map.
  }

  /**
   * Handler for open documents event, ONLY for opening documents in the external viewer.
   *
   * @param openRequest Request to open documents.
   */
  onOpenInboxDocuments(openRequest?: InboxDocumentOpenRequest): void {
    this.logger.debug(
      'Opening selected documents in external viewer.',
      openRequest
    );
    assertExists(openRequest?.inboxFiles);
    // Use an external viewer session.
    const session: InboxSession = {
      database: this.databasesQuery.activeId,
      documents: openRequest?.inboxFiles.map((inboxFile) =>
        createInboxSessionDocumentFromInboxFile(
          inboxFile,
          this.inboxesQuery.activeId
        )
      ),
    };

    this.viewerService.createInboxSession(session).subscribe({
      next: (sessionId) => {
        this.viewerService
          .openViewerSession(sessionId, this.appQuery.alwaysOpenNewTab)
          .subscribe(() => {
            this.logger.debug('Viewer closed.');
            this.onRefreshSearch({});
          });
      },
      error: (error: UserFriendlyError) => {
        error.i18n = 'ERROR_CREATE_SESSION_MSG';
        this.notify.error(error);
      },
    });
  }

  /**
   * Handler for open documents event, ONLY for opening documents in the external viewer.
   *
   * @param openRequest Request to open documents.
   */
  onOpenSearchResults(openRequest?: SearchResultDocumentOpenRequest): void {
    assertExists(openRequest?.searchResults);
    this.searchesQuery.searchRouteParams$
      .pipe(first())
      .subscribe(([_databaseId, archiveId, searchId]) => {
        assertExists(archiveId);
        assertExists(searchId);

        this.createOpenDocumentSession(
          archiveId,
          searchId,
          openRequest?.searchResults
        );
      });
  }

  /**
   * Handler for open task view documents event, ONLY for opening documents in the external viewer.
   *
   * @param openRequest Request to open documents.
   */
  onOpenTaskViewDocuments(openRequest?: SearchResultDocumentOpenRequest): void {
    assertExists(openRequest?.searchResults);
    this.viewerService
      .openSelectedArchiveDocuments(
        this.databasesQuery.activeId,
        this.archivesQuery.activeId,
        0,
        [],
        openRequest.searchResults,
        this.appQuery.alwaysOpenNewTab
      )
      .subscribe({
        next: () => {
          this.logger.debug('Viewer closed.');
          this.onRefreshSearch({ forceReload: true });
        },
        error: (error: UserFriendlyError) => {
          error.i18n = 'ERROR_CREATE_SESSION_MSG';
          this.notify.error(error);
        },
      });
  }

  /**
   * Handler for the page change event.
   *
   *@param pageNumber New page number.
   */
  onPageChange(pageNumber: number): void {
    this.pageNumber = pageNumber;
    // Change detection in this event context isn't automatically running for some reason
    // so we need to force it here.
    this.changeDetectorReference.detectChanges();
    // Change the annotation service page.
    this.annotationService.changePage(this.pdfviewer, pageNumber);
  }

  onPdfModified(event: PdfModifiedEvent) {
    assertExists(this.pdfOptions, 'PDF options must exist.');
    this.documentDirty = true;
    this.pdfOptions = {
      url: createDataUrl(event.modifiedPdfBytes),
      // Pass only annotations that have not been marked for burn to prevent the viewer
      // from immediately burning them.
      annotations: this.annotationService.annotations.filter((a) => !a.burn),
    };

    if (this.shouldAutoSave) {
      this.autoSaveDocumentChanges(event.modifiedPdfBytes);
      this.autoSaveAnnotationChanges();
    }
  }

  /**
   * Handler for refresh search.
   *
   * @param event Event.
   * @param event.ids Document Ids deleted.
   * @param event.filenames Files deleted.
   * @param event.forceReload Reload will be forced for the same route.
   */
  onRefreshSearch(event: {
    /** Whether to **only** reload index data. This will prevent any other options in this event from having any effect. */
    reloadOnlyDocViewIndexData?: boolean;
    /** Removed document filenames. */ filenames?: string[];
    /** Force reload. */ forceReload?: boolean;
    /** Removed document Ids. */ ids?: number[];
    /** Added document Ids. These **must** be in the current archive to work properly. */
    idsToAdd?: number[];
    /** The id of a document the emitter of this event is requesting be displayed to the user. */
    requestedViewDocument?: number;
  }) {
    if (event?.reloadOnlyDocViewIndexData) {
      this.logger.debug('Loading only document view index data.');
      this.reloadDocumentData().subscribe(() => {
        this.logger.debug(
          'Document data was retrieved from the server. Reloading the indexer.'
        );
        this.changeDetectorReference.detectChanges();
        this.rightSidebar?.indexer?.reloadIndexerFields();
      });
      return;
    }
    // If the document is no longer here after an action (move/delete),
    // remove it from the session, or return to search results.
    if (event?.ids?.length) {
      // Remove documents by Id.
      this.logger.debug('Removing Ids: ', event.ids);
      // Remove any Ids that were targetted.
      for (let id of event.ids) this.requestedDocuments.deleteValue(id);
    }
    if (event?.filenames?.length) {
      // Remove documents by filename.
      this.logger.debug('Removing filenames: ', event.filenames);
      // Remove any filenames that were targetted.
      for (let filename of event.filenames)
        this.requestedDocuments.deleteValue(filename);

      // If there are no documents left, just return to the search.
      if (this.requestedDocuments.size === 0) {
        this.logger.debug('No remaining documents, return to search.');
        this.onClickReturnToSearch();
        return;
      }
    }

    if (event.idsToAdd) {
      this.logger.debug('Adding Ids: ', event.idsToAdd);
      for (const id of event.idsToAdd) {
        this.requestedDocuments.addValue(id);
      }
    }

    // If there are no documents left, just return to the search.
    if (this.requestedDocuments.size === 0) {
      this.logger.debug('No remaining documents, return to search.');
      this.onClickReturnToSearch();
      return;
    }

    // Save the current index before making changes to it in case navigation fails.
    // Also, account for if the index is currently out of bounds due to document removals, in which case set it to the last document.
    const originalViewIndex = Math.min(
      this.requestedDocuments.size - 1,
      this.viewIndex
    );

    if (event.requestedViewDocument) {
      this.logger.debug(
        'Setting requested view document: ',
        event.requestedViewDocument
      );

      const requestedIndex = this.requestedDocuments.indexOf(
        event.requestedViewDocument
      );

      if (requestedIndex === -1) {
        this.logger.error(
          'Unable to set requested view document because it does not exist in the list.'
        );
      } else {
        this.viewIndex = requestedIndex;
      }
    } else {
      // Update selection, ensure we don't select past the end of the list.
      this.viewIndex = Math.min(
        this.requestedDocuments.size - 1,
        this.viewIndex
      );
    }

    // Reload the route.
    this.navigateToDocument(!!event?.forceReload).subscribe(
      (navigationSuccessful) => {
        if (!navigationSuccessful) {
          this.viewIndex = originalViewIndex;
        }
      }
    );
  }

  /**
   * Handler for related search selected event.
   *
   * @param event related search selection event.
   */
  onRelatedSearchSelected(event: RelatedSearchSelectedEvent): void {
    const { search, openInNewTab } = event;
    this.logger.debug('Related search selected: ', search);
    assertExists(this.rightSidebar.indexer, 'The indexer must exist.');
    this.searchesQuery.searchPromptParams$
      .pipe(take(1))
      .subscribe((querySearchPrompts) => {
        const prompts = search.parameters.map((parameter) => {
          const searchPrompt = {
            id: parameter.id,
            prompt: parameter.prompt,
            value: '',
          };

          // If the setting is on just use the values from the url.
          if (this.appQuery.usePreviousSearchCriteriaInRelatedSearch) {
            // Check if the prompt is in the url search prompts
            const queryPrompt = querySearchPrompts.find(
              (prompt) => prompt.id === parameter.id
            );
            if (queryPrompt) {
              searchPrompt.value = queryPrompt.value;
            }

            return searchPrompt;
          }

          // The usePreviousSearchCriteriaInRelatedSearch setting is off if we get here.

          // check if the field is in the indexer.
          const indexerField = this.rightSidebar.indexer.indexerFields.find(
            (field) => field.id === parameter.fieldId
          );
          if (indexerField) {
            // Grab the first mv field form control or the single field form control.
            const formControl = indexerField.multiValue
              ? this.rightSidebar.indexer.getMultiValueFormArray(
                  parameter.fieldId
                ).controls[0]
              : this.rightSidebar.indexer.indexerForm.get(
                  `${parameter.fieldId}`
                );
            assertExists(
              formControl,
              'A form control for the field must exist.'
            );
            searchPrompt.value = formControl.value ?? '';
          }

          return searchPrompt;
        });

        if (openInNewTab) {
          this.searchUIService.openSearchInNewTab(
            this.databaseId,
            this.archiveId,
            search.id,
            prompts
          );
        } else {
          this.searchUIService.redirectToSearch(
            this.databaseId,
            this.archiveId,
            search.id,
            prompts
          );
        }
      });
  }

  /**
   * Handler for the table data save event.
   */
  onTableDataSaved(): void {
    if (this.shouldAutoSave && this.indexerReloadAfterSave) {
      /*
      Trigger live field reloads during auto save because never automatically reload
      the document during an auto save like we do if the user hits the save button.
      We must also reload document data to avoid issues where regular index data auto saved in this session
      could be reverted by the indexer reload.
      */
      this.reloadDocumentData().subscribe(() => {
        this.changeDetectorReference.detectChanges();
        this.rightSidebar?.indexer?.reloadIndexerFields();
      });
    }
  }

  /**
   * Handler for the user action executed event.
   *
   * @param event User action event.
   */
  onUserActionExecuted(event: UserActionExecutedEvent): void {
    this.logger.debug(
      'User action executed. The document will be closed.',
      event
    );

    let shouldContinue$: Observable<boolean> = of(true);

    if (this.isDirty) {
      if (this.rightSidebar?.indexer?.indexerForm.invalid) {
        this.notify.error({
          i18n: 'USER_ACTION_EXECUTION_CANCELLED_INDEXER_ERRORS_MSG',
          error: new Error('Indexer form has errors.'),
          description: 'The indexer has errors.',
        });
        shouldContinue$ = of(false);
      }
      if (this.appQuery.viewerAutoSave) {
        // Save the document changes
        this.saveInProgress = true;

        const save$ = this.createSave$().pipe(
          finalize(() => {
            this.saveInProgress = false;
          })
        );
        shouldContinue$ = shouldContinue$.pipe(
          switchMap((shouldContinue) => {
            if (!shouldContinue) {
              this.saveInProgress = false;
              // Ensure that save is cancelled if a previous observable value already told us to stop.
              return of(false);
            }
            return save$.pipe(
              catchError(() => of(false)), // Do not continue if save failed.
              map(() => true)
            );
          })
        );
      } else if (this.shouldAutoSave) {
        // We do not want to trigger the discard prompt if changes will just be auto saved.
        shouldContinue$ = of(true);
      } else {
        shouldContinue$ = shouldContinue$.pipe(
          switchMap((shouldContinue) => {
            // Ensure that confirmation isn't prompted if the action already should **not** continue.
            if (!shouldContinue) {
              return of(false);
            }

            // Confirm changes.
            const confirmDialogData: ConfirmationDialogData = {
              cancelActionText: 'NO',
              confirmActionText: 'YES',
              contents: 'CONFIRM_DISCARD_CHANGES_MSG',
              title: 'DISCARD_CHANGES',
            };
            return this.dialog
              .open(ConfirmationDialogComponent, { data: confirmDialogData })
              .afterClosed();
          })
        );
      }
    }

    shouldContinue$ = shouldContinue$.pipe(
      switchMap((shouldContinue) => {
        if (!shouldContinue) {
          event.cancelAction();
          throw new Error('User action execution was cancelled by the user.');
        }
        return event.runAction().pipe(
          catchError(() => of(false)),
          map(() => true)
        );
      })
    );

    shouldContinue$.subscribe((shouldContinue) => {
      if (!shouldContinue) {
        this.logger.warn('GA action execution was cancelled by the user.');
        return;
      }

      if (this.isDirty) {
        // If we get here it means auto save wasn't on and that the user agreed to discard changes.
        // Force the indexer to appear clean to prevent a second discard prompt from the dirty guard.
        this.rightSidebar?.indexer?.indexerForm.markAsPristine();
        // Force any table field to appear clearn to prevent a second discard prompt form the dirty guard.
        this.tableFieldGrid()?.forceSetGridDirty(false);
      }

      // Remove the current document from the open documents since the action was executed on it.
      this.requestedDocuments.delete(this.viewIndex);

      // Go back if there are no more documents.
      if (this.requestedDocuments.size === 0) {
        this.onClickReturnToSearch();
        return;
      }

      // Otherwise set the new set of open documents and reload.
      this.navigateToDocument().subscribe();
    });
  }

  reloadDocumentData(): Observable<SearchResult> {
    const mode = this.displayMode;
    switch (mode) {
      case 'archive':
        const searchResult$ = this.runSearchForSingleDocument(
          this.archiveId,
          this.searchId,
          this.searchesQuery.currentSearchPrompts,
          0,
          (this.document as SearchResult).id
        );
        searchResult$
          .pipe(
            map((result) => {
              this.logger.debug(
                'Reload document data: updated document',
                result
              );
              this.document = result;
              return result;
            })
          )
          .subscribe();
        return searchResult$;
      default:
        // Currently only the "already archived" state of the indexer should be a relevant context.
        this.logger.error(
          'Reload document data is not supported for this document type {}',
          mode
        );
        return throwError(
          () =>
            new Error(
              `Reload document data is not supported for this document type ${mode}`
            )
        );
    }
  }

  private autoSave$(): Observable<boolean> {
    if (this.displayMode === 'inbox') {
      this.logger.warn('Saving inbox document changes are not yet supported.');
    }
    if (!this.isDirty) {
      this.logger.debug(
        'Dirty function was called but the component is not dirty.'
      );
      // The component isn't dirty so there is no need to run the dirty function.
      return of(true);
    }

    if (this.rightSidebar.indexer.indexerForm.invalid) {
      this.logger.warn('Indexer form has errors.');
      return of(false);
    }

    const save$ = this.createSave$();
    return save$.pipe(
      map(() => {
        this.notify.success('SAVE_SUCCESS');
        return true;
      }),
      catchError((error: UserFriendlyError) => {
        this.notify.error(error);
        return of(false);
      })
    );
  }

  private autoSaveAnnotationChanges(): void {
    if (!this.documentPermissions.modifyAnnotations) {
      this.logger.debug(
        'User does not have permission to modify annotations. No changes will be saved automatically.'
      );
      return;
    }

    const workflowQueuedDocument = this.workflowQueuedDocument();

    if (
      this.isAnnotationDirty &&
      workflowQueuedDocument &&
      !workflowQueuedDocument.userIsSecuredToQueue
    ) {
      // Users that are not secured to a queue cannot make document modifications or annotation changes so ensure the dirty state is reset to avoid save.
      this.logger.warn(
        'The user has made annotation changes but is not secured to the workflow queue. Changes will NOT be saved.'
      );
      this.annotationService.dirty$.next(false);
      return;
    }

    this.logger.debug('Saving annotation changes automatically.');
    const documentSearchResult = this.documentAsSearchResult;

    // Ensure the pdf viewer has the annotations
    this.pdfviewer.documentAnnotations = [
      ...this.annotationService.annotations,
    ];
    let hasBurnedAnnotations = this.pdfviewer.documentAnnotations.some(
      (a) => a.burn
    );

    if (hasBurnedAnnotations && !this.documentPermissions.modifyDocuments) {
      this.logger.debug(
        'User does not have permission to modify documents for burning annotations. No changes will be saved automatically.'
      );
      return;
    }

    if (hasBurnedAnnotations) {
      // If the document is encrypted, annotations cannot be burned.
      if (this.pdfviewer.isDocumentEncrypted) {
        this.notify.error(
          new UserFriendlyError(
            'Cannot burn annotations on encrypted documents.',
            'Burning annotations to an encrypted or signed document will corrupt the document.',
            'PDF_DOCUMENT_ENCRYPTED_NO_BURN'
          )
        );
        // Remove the burn flag from the annotations so they are not burned by the API either.
        for (const annotation of this.pdfviewer.documentAnnotations) {
          annotation.burn = false;
        }
        // Update the check.
        hasBurnedAnnotations = false;
      }
    }

    const updateSession = new DocumentUpdateSession(
      true,
      hasBurnedAnnotations, // update document if there are burned annotations
      false,
      false
    );

    const documentChangesSave$ = hasBurnedAnnotations
      ? this.createUpdateDocument$(updateSession).pipe(
          map((result) => !!result)
        )
      : of(false);

    documentChangesSave$
      .pipe(
        switchMap(() =>
          this.createAnnotationSave$(
            this.archiveId,
            documentSearchResult.id,
            documentSearchResult.secureId,
            updateSession
          )
        )
      )
      .subscribe({
        next: () => {
          this.notify.success('ANNOTATION_CHANGES_SAVED');
        },
        error: (error: UserFriendlyError) => {
          this.notify.error(error);
        },
      });
  }

  private autoSaveDocumentChanges(
    modifiedPdfBytes: Uint8Array<ArrayBufferLike>
  ): void {
    if (!this.documentPermissions.modifyDocuments) {
      // Make absolutely certain that the user has permissions to modify documents.
      this.logger.debug(
        'User does not have modify documents permissions. Document changes will not be saved automatically.'
      );
      return;
    }

    const workflowQueuedDocument = this.workflowQueuedDocument();

    if (
      this.documentDirty &&
      workflowQueuedDocument &&
      !workflowQueuedDocument.userIsSecuredToQueue
    ) {
      // Users that are not secured to a queue cannot make document modifications or annotation changes so ensure the dirty state is reset to avoid save.
      this.logger.warn(
        'The user has made annotation changes but is not secured to the workflow queue. Changes will NOT be saved.'
      );
      this.documentDirty = false;
      return;
    }

    this.logger.debug('Saving document changes automatically.');
    const updateSession = new DocumentUpdateSession(false, true, false, false);

    this.createUpdateDocument$(updateSession, modifiedPdfBytes).subscribe({
      next: () => {
        this.notify.success('DOCUMENT_CHANGES_SAVED');
      },
      error: (error: UserFriendlyError) => {
        this.notify.error(error);
      },
    });
  }

  private autoSaveIndexData(field: Field): void {
    if (!this.documentPermissions.modifyData) {
      // Make absolutely certain that the user has permissions to modify data.
      this.logger.debug(
        'User does not have modify data permissions. Index data will not be saved automatically.'
      );
      return;
    }
    const control = field.multiValue
      ? this.rightSidebar.indexer.getMultiValueFormArray(field.id)
      : this.rightSidebar.indexer.getFormControl(field.id);

    if (control.dirty) {
      this.logger.debug('Blurred indexer field was dirty.', field);
      if (this.rightSidebar.indexer.disableSave) {
        this.logger.debug(
          'Saving indexer changes is disabled. Changes will not be saved automatically.'
        );
        return;
      }

      if (!this.rightSidebar.indexer.isDirty) {
        // Prevent saves if the indexer isn't dirty.
        // This should never be reached because we check if the control is dirty above.
        this.logger.debug(
          'Indexer is not dirty. Changes will not be saved automatically.'
        );
        return;
      }

      const documentSearchResult = this.documentAsSearchResult;
      const updateSession = new DocumentUpdateSession(
        false,
        false,
        true,
        false
      );
      this.createSaveIndexData$(
        this.archiveId,
        documentSearchResult.id,
        documentSearchResult.secureId,
        updateSession
      ).subscribe({
        next: () => {
          this.notify.success('FIELD_DATA_SAVED');
          this.rightSidebar.indexer.reloadLiveFields();
        },
        error: (error: UserFriendlyError) => {
          this.notify.error(error);
        },
      });
    }
  }

  /**
   * Create a burn annotations observable that will burn any annotations marked for burn to the
   * document and then update its bytes in the viewer.
   */
  createBurnAnnotations(): Observable<boolean> {
    return defer(async () => {
      // Create a defer here to allow the creation of an observable that does the synchronous burnAnnotations call
      // as well as the async saveLocalPdfChanges call without immediately executing it so that the save pipeline can
      // create observables and conditionally apply them to the save pipeline.
      await this.pdfviewer.burnAnnotations();
      if (!this.pdfviewer.hasBurnedAnnotations) {
        // Don't bother doing anything and tell
        return false;
      }

      // Convert the last value emitted from the saveLocalPdfChanges observable to a promise so defer can work with it.
      // The underlying observable is already a promise converted to an observable via defer so there should only ever be one emission any way.
      await firstValueFrom(this.pdfviewer.saveLocalPdfChanges());
      // Refresh the annotation service's copy of the annotations so the burned annotations are removed.
      this.annotationService.loadAnnotations(
        this.pdfviewer.documentAnnotations,
        true
      );
      // Trigger a redraw so the burned annotations are removed.
      this.annotationService.redrawAnnotations(true);
      return true;
    });
  }

  private createOpenDocumentSession(
    archiveId: number,
    searchId: number,
    searchResults: SearchResults
  ): void {
    this.viewerService
      .openSelectedArchiveDocuments(
        this.databasesQuery.activeId,
        archiveId,
        searchId,
        this.searchesQuery.currentSearchPrompts,
        searchResults,
        this.appQuery.alwaysOpenNewTab
      )
      .subscribe({
        next: () => {
          this.logger.debug('Viewer closed.');
          this.onRefreshSearch({ forceReload: true });
        },
        error: (error: UserFriendlyError) => {
          error.i18n = 'ERROR_CREATE_SESSION_MSG';
          this.notify.error(error);
        },
      });
  }

  private createSave$(): Observable<{
    /** Document id. */
    documentId: number;
    /** Document secureId. */
    documentSecureId: string;
  }> {
    let hasBurnedAnnotations = false;
    // Ensure pdf viewer exists
    if (this.pdfviewer) {
      // Ensure pdf viewer has the annotations
      this.pdfviewer.documentAnnotations = [
        ...this.annotationService.annotations,
      ];
      hasBurnedAnnotations = this.pdfviewer.documentAnnotations.some(
        (a) => a.burn
      );
      if (hasBurnedAnnotations) {
        // If the document is encrypted, annotations cannot be burned.
        if (this.pdfviewer.isDocumentEncrypted) {
          this.notify.error(
            new UserFriendlyError(
              'Cannot burn annotations on encrypted documents.',
              'Burning annotations to an encrypted or signed document will corrupt the document.',
              'PDF_DOCUMENT_ENCRYPTED_NO_BURN'
            )
          );
          // Remove the burn flag from the annotations so they are not burned by the API either.
          for (const annotation of this.pdfviewer.documentAnnotations) {
            annotation.burn = false;
          }
          // Update the check.
          hasBurnedAnnotations = false;
        } else {
          // Force the document to be dirty because the burned annotations need to be saved to the API.
          this.documentDirty = true;
        }
      }

      if (this.documentDirty) {
        // Always save annotations if the document has changed to ensure annotations match the document.
        // This applies even if there are no annotations because they may have been removed during something like a page delete.
        this.annotationService.dirty$.next(true);
      }
    }

    const workflowQueuedDocument = this.workflowQueuedDocument();

    if (
      (this.isAnnotationDirty || this.documentDirty) &&
      workflowQueuedDocument &&
      !workflowQueuedDocument.userIsSecuredToQueue
    ) {
      // Users that are not secured to a queue cannot make document modifications or annotation changes so ensure the dirty state is reset to avoid save.
      this.logger.warn(
        'The user has made annotation changes but is not secured to the workflow queue. Changes will NOT be saved.'
      );
      this.documentDirty = false;
      this.annotationService.dirty$.next(false);
    }

    let import$: Observable<ImportedArchiveDocument>;
    const updateSession = new DocumentUpdateSession(
      this.isAnnotationDirty,
      this.documentDirty,
      this.rightSidebar?.indexer?.isDirty ?? false,
      this.tableFieldGrid()?.isDirty ?? false
    );

    if (this.displayMode === 'inboxIndexing') {
      import$ = this.inboxesService.api.indexToArchive(
        this.inboxId,
        `${this.documentAsInboxFile.filename}${this.documentAsInboxFile.fileType}`,
        this.databaseId,
        this.inboxTargetArchiveId,
        updateSession
      );
    } else if (this.displayMode === 'archiveImport') {
      // Determine whether to use the file in the API cache for import or use the byte array.
      const useByteImport =
        hasBurnedAnnotations || this.forceImportToUsePdfBytes;
      if (hasBurnedAnnotations) {
        // Burn the annotations and then import the file using the bytes in the pdf viewer.
        import$ = this.createBurnAnnotations().pipe(
          switchMap(() =>
            this.archiveService.api.importBytes(
              this.databaseId,
              this.archiveId,
              this.pdfviewer.pdfBytes,
              '.pdf',
              'application/pdf',
              updateSession
            )
          )
        );
      } else {
        import$ = useByteImport
          ? this.archiveService.api.importBytes(
              this.databaseId,
              this.archiveId,
              this.pdfviewer.pdfBytes,
              '.pdf',
              'application/pdf',
              updateSession
            )
          : this.archiveService.api
              .import(
                this.databaseId,
                this.archiveId,
                [this.documentAsArchiveImportFile.filename],
                updateSession
              )
              .pipe(map((importedDocuments) => importedDocuments[0]));
      }
    } else if (this.documentDirty) {
      import$ = this.createUpdateDocument$(updateSession);
    } else {
      import$ = of<ImportedArchiveDocument>({
        id: this.documentAsSearchResult.id,
        secureId: this.documentAsSearchResult.secureId,
      });
    }

    const save$ = import$.pipe(
      switchMap((document) => {
        assertExists(
          document,
          'Document object with id and secure id must be provided to save.'
        );

        this.documentDirty = false;

        const saveChain$ = this.createSaveChain(
          document.id,
          document.secureId,
          updateSession
        );

        return saveChain$.pipe(
          switchMap(() =>
            of({ documentId: document.id, documentSecureId: document.secureId })
          )
        );
      })
    );

    return save$;
  }

  /**
   * Creates an observable that contains all update calls to be run in order.
   *
   * @param documentId Document id.
   * @param secureId Document secure id.
   * @param session Document Update Session.
   * @returns An observable that runs each update sequentially.
   */
  private createSaveChain(
    documentId: number,
    secureId: string,
    session: DocumentUpdateSession
  ) {
    const saveChain: Observable<void>[] = [];
    const archiveId =
      this.displayMode === 'inboxIndexing'
        ? this.inboxTargetArchiveId
        : this.archiveId;
    if (this.rightSidebar?.indexer?.isDirty) {
      const fieldUpdate$ = this.createSaveIndexData$(
        archiveId,
        documentId,
        secureId,
        session
      );

      saveChain.push(fieldUpdate$);
    }

    const tableFieldGrid = this.tableFieldGrid();

    if (tableFieldGrid?.isDirty) {
      const tableUpdate$ = tableFieldGrid
        .saveTableData(documentId, secureId, session)
        .pipe(
          catchError((error: UserFriendlyError) => {
            error.i18n = 'FAILED_TO_SAVE_TABLE_DATA';
            return throwError(() => error);
          }),
          tap(() => {
            if (
              this.displayMode === 'archiveImport' ||
              this.displayMode === 'inboxIndexing'
            ) {
              tableFieldGrid?.clearData(false);
            }
          })
        );
      saveChain.push(tableUpdate$);
    }

    if (this.isAnnotationDirty && this.annotationsSupported) {
      const annotationUpdate$ = this.createAnnotationSave$(
        archiveId,
        documentId,
        secureId,
        session
      );
      saveChain.push(annotationUpdate$);
    }

    // This will create an observable that will run each observable in the chain, and emit when all finish.
    // If an observable errors then the chain will stop.
    // If an observable never completes then this observable will also never complete.
    return forkJoin(saveChain);
  }

  private createAnnotationSave$(
    archiveId: number,
    documentId: number,
    secureId: string,
    session: DocumentUpdateSession
  ) {
    const annotationsForSave = this.annotationService.getAnnotationsForSave();
    return this.documentUpdateProvider
      .updateAnnotationData(
        this.databaseId,
        archiveId,
        documentId,
        secureId,
        annotationsForSave,
        session
      )
      .pipe(
        catchError((error: UserFriendlyError) => {
          error.i18n = 'FAILED_TO_SAVE_ANNOTATION_DATA';
          return throwError(() => error);
        }),
        tap(() => {
          this.annotationService.setDirtyStatus(false);
          this.annotationService.redrawAnnotations(true);
        })
      );
  }

  private createSaveIndexData$(
    archiveId: number,
    documentId: number,
    secureId: string,
    session: DocumentUpdateSession
  ) {
    const fieldValues = this.rightSidebar.indexer.getFieldValuesForSave();
    return this.documentUpdateProvider
      .updateFieldData(
        this.databaseId,
        archiveId,
        documentId,
        secureId,
        fieldValues,
        session
      )
      .pipe(
        catchError((error: UserFriendlyError) => {
          this.logger.error(
            'An error occurred while attempting to save index data.',
            error
          );
          error.i18n = 'FAILED_TO_SAVE_INDEX_DATA';
          return throwError(() => error);
        }),
        tap(() => {
          this.logger.debug('Field data updated successfully.');
          if (
            this.displayMode === 'archiveImport' ||
            this.displayMode === 'inboxIndexing'
          ) {
            this.rightSidebar.indexer.indexerForm.reset();
          } else {
            this.rightSidebar.indexer.indexerForm.markAsPristine();
          }
        })
      );
  }

  /**
   * Creates an observable that will confirms the save if pdf conversion occurs and then updates the document.
   *
   * @param updateSession Update session.
   * @param modifiedPdfBytes Optional Modified pdf bytes. This uses the bytes from the pdf viewer if not provided.
   * @returns An observable containing document id and secure id.
   */
  private createUpdateDocument$(
    updateSession: DocumentUpdateSession,
    modifiedPdfBytes?: Uint8Array<ArrayBufferLike>
  ) {
    const fileNameForUpload = `${uuid()}.pdf`;
    const overwriteConfirmation$ =
      this.documentAsSearchResult.fileType.toLowerCase() === '.pdf'
        ? of(true)
        : this.dialog
            .open(ConfirmationDialogComponent, {
              data: {
                cancelActionText: 'CANCEL',
                confirmActionText: 'OK',
                contents: 'CONFIRM_ORIGINAL_FILE_OVERWRITE_MSG',
                title: 'SAVE_OVERWRITE_ORIGINAL_FILE_TITLE',
              },
            })
            .afterClosed()
            .pipe(
              map((result) => {
                if (!result) {
                  throw new UserFriendlyError(
                    'User cancelled save due to overwrite confirmation.',
                    'Save was cancelled by user',
                    'SAVE_CANCELLED_BY_USER'
                  );
                }

                return true;
              })
            );
    return overwriteConfirmation$.pipe(
      // confirmed should always be true here since we throw an error above if it is false
      switchMap((confirmed) => {
        // Ensure the PDF is saved with the burned annotations and then return the confirmed state again.
        if (this.pdfviewer.documentAnnotations.some((a) => a.burn)) {
          return this.createBurnAnnotations().pipe(map(() => confirmed));
        }

        // There are no annotations being burned so just pass the confirmed state to the rest of the pipe.
        return of(confirmed);
      }),
      switchMap((confirmed) =>
        this.documentUpdateProvider
          .updateDocument(
            this.databaseId,
            this.archiveId,
            this.documentAsSearchResult.id,
            this.documentAsSearchResult.secureId,
            modifiedPdfBytes ?? this.pdfviewer.pdfBytes,
            fileNameForUpload,
            'application/pdf',
            updateSession
          )
          .pipe(
            map(() => ({
              id: this.documentAsSearchResult.id,
              secureId: this.documentAsSearchResult.secureId,
            })),
            tap(() => {
              this.documentDirty = false; // reset document dirty state
            })
          )
      )
    );
  }

  private getArchiveImportFile(viewIndex: any, documentId: any) {
    this.requestedDocuments = new ArchiveImportRequestedDocumentMap(documentId);
    // Get the index, but do not exceed the set.
    this.viewIndex = Math.min(
      Number(viewIndex ?? 0),
      this.requestedDocuments.size - 1
    );
    const archiveImportFile: ArchiveImportFile = {
      filename: this.requestedDocuments.get(
        this.viewIndex
      ) as ArchiveImportRequestedDocument,
    };
    this.logger.debug('archive file import', archiveImportFile);
    this.document = archiveImportFile;
    this.loadImportDocumentAsPdf(archiveImportFile.filename);
    return of(archiveImportFile);
  }

  // We need to know the locales decimal seperator character (can't just assume .)
  private getDecimalSeparator(locale: any) {
    const numberWithDecimalSeparator = 1.1;
    const numberFormat = Intl.NumberFormat(locale);
    assertExists(numberFormat, 'Number format must exist.');
    const formatParts = numberFormat.formatToParts(numberWithDecimalSeparator);
    assertExists(formatParts, 'Must be able to format to parts.');
    return formatParts.find((part) => part.type === 'decimal')?.value;
  }

  private getNextField() {
    const currentField = this.rightSidebar.indexer.lastFocusedField,
      indexerFields = this.rightSidebar.indexer.indexerFields;
    let index = currentField
      ? indexerFields.findIndex((f) => f.id == currentField.id) + 1
      : 0;
    let field: IndexerField | undefined =
      this.rightSidebar.indexer.indexerFields[index];
    if (!field && index >= this.rightSidebar.indexer.indexerFields.length) {
      // No more fields to iterate through.
      // Field will be undefined at this point.
      return undefined;
    }
    let fieldControl = this.rightSidebar.indexer.getFormControl(field.id);

    // Skip any field disabled in the indexer form.
    while (field && fieldControl.disabled) {
      index++;
      field = this.rightSidebar.indexer.indexerFields[index];
      if (!field && index >= this.rightSidebar.indexer.indexerFields.length) {
        // No more fields to iterate through.
        // Field will be undefined at this point.
        break;
      }
      fieldControl = this.rightSidebar.indexer.getFormControl(field.id);
    }

    return field;
  }

  private getSearchResultForArchiveRoute(
    archiveId: number,
    databaseId: number,
    searchId: number,
    searchPrompts: SearchPrompt[],
    documentId: string,
    viewIndex: number
  ) {
    this.logger.debug('Loading single document search for:', {
      databaseId,
      archiveId,
      searchId,
      searchPrompts,
      documentId,
      viewIndex,
    });

    // Support multiple documents.
    this.requestedDocuments = new ArchiveSearchRequestedDocumentMap(documentId);
    // Get the index, but do not exceed the set.
    this.viewIndex = Math.min(
      Number(viewIndex ?? 0),
      this.requestedDocuments.size - 1
    );
    const id = this.requestedDocuments.get(
      this.viewIndex
    ) as ArchiveSearchRequestedDocument;
    assertExists(id, 'Document Id is required.');
    // Search for the matching document, return it as the final value of
    // this Observable, and trigger a load of the document.
    return this.runSearchForSingleDocument(
      archiveId,
      searchId,
      searchPrompts,
      0,
      id
    ).pipe(
      tap((document) => {
        this.loadDocumentAsPdf(databaseId, archiveId, document);
      })
    );
  }

  private getSearchResultForInboxRoute(
    databaseId: number,
    inboxId: any,
    documentId: any,
    viewIndex: any
  ) {
    this.logger.debug('Loading inbox document for:', {
      databaseId,
      inboxId,
      documentId,
      viewIndex,
    });

    // Support multiple documents.
    this.requestedDocuments = new InboxRequestedDocumentMap(documentId);
    // Get the index, but do not exceed the set.
    this.viewIndex = Math.min(
      Number(viewIndex ?? 0),
      this.requestedDocuments.size - 1
    );
    // Get the matching inbox file.
    return this.inboxesQuery.activeInbox$.pipe(
      first(),
      switchMap((inbox) => {
        /**
         * We need to run the get inbox call on a specific inbox to get its files if they
         are not already in the store.
         */
        return inbox.files.length > 0
          ? of(inbox)
          : this.inboxesService.getById(inbox.id);
      }),
      map((inbox) => {
        const documentName = this.requestedDocuments.get(
          this.viewIndex
        ) as InboxRequestedDocument;
        const requestedFileName = decodeURIComponent(documentName);
        const matchingFile = inbox.files.find(
          (file) => file.filename + file.fileType === requestedFileName
        );
        try {
          assertExists(matchingFile, 'No matching inbox file.');
          this.document = matchingFile;
          // Request the image and load it in the viewer.
          this.loadDocumentAsPdfFromInbox(inboxId, documentName);
          return matchingFile;
        } catch {
          // Document not found. Remove from URL.
          this.onLoadFailure();
        }
      })
    );
  }

  private getSearchResultForTaskSearchRoute(
    databaseId: number,
    archiveId: number,
    documentId: any,
    viewIndex: any
  ) {
    this.logger.debug('Loading single document task search for:', {
      databaseId,
      archiveId,
      documentId,
      viewIndex,
    });

    // Support multiple documents.
    this.requestedDocuments = new ArchiveSearchRequestedDocumentMap(documentId);

    // Get the index, but do not exceed the set.
    this.viewIndex = Math.min(
      Number(viewIndex ?? 0),
      this.requestedDocuments.size - 1
    );

    const id = this.requestedDocuments.get(
      this.viewIndex
    ) as ArchiveSearchRequestedDocument;

    assertExists(id, 'Document Id is required.');

    return this.taskSearchesService
      .getQueuedDocument(databaseId, archiveId, id)
      .pipe(
        map((result: SearchResult) => {
          this.logger.debug('Search results:', result);
          assertExists(result, 'No document was found matching query.');
          return result;
        }),
        tap((document) => {
          this.loadDocumentAsPdf(databaseId, archiveId, document);
        })
      );
  }

  private getSearchResultForArchiveRevision(
    databaseId: number,
    archiveId: number,
    documentIdRouteString: string
  ): Observable<SearchResult> {
    // Support multiple documents.
    this.requestedDocuments = new ArchiveRevisionRequestedDocumentMap(
      documentIdRouteString as string
    );

    // Get the index, but do not exceed the set.
    this.viewIndex = Math.min(
      Number(this.viewIndex ?? 0),
      this.requestedDocuments.size - 1
    );

    const document = this.requestedDocuments.get(
      this.viewIndex
    ) as ArchiveRevisionRequestedDocument;

    if (document.versionNumber === CURRENT_DOCUMENT_REVISION) {
      // This is the current version so don't bother with loading previous revisions.
      return this.searchesService
        .getDocumentData(
          databaseId,
          this.archiveId,
          document.docId,
          document.secureId
        )
        .pipe(
          tap((document) => {
            this.loadDocumentAsPdf(databaseId, this.archiveId, document);
          })
        );
    }

    return this.documentProvider
      .getDocumentRevisions(
        databaseId,
        archiveId,
        document.docId,
        document.secureId,
        this.auth.user.token
      )
      .pipe(
        switchMap((revisions) => {
          const revision = revisions.find(
            (r) => r.version === document.versionNumber
          );
          assertExists(
            revision,
            'Revision with provided verison number must exist.'
          );
          this.revisionArchive = this.archivesQuery.getVersionsArchive();
          return this.searchesService
            .getDocumentData(
              databaseId,
              this.revisionArchive.id,
              revision.documentId,
              revision.secureId
            )
            .pipe(
              tap((document) => {
                this.loadDocumentAsPdf(
                  databaseId,
                  this.revisionArchive.id,
                  document
                );
              })
            );
        })
      );
  }

  private getSearchResultFromArchiveDocument(
    databaseId: number,
    archiveId: number,
    documentId: string
  ): Observable<SearchResult> {
    // Support multiple documents.
    this.requestedDocuments = new ArchiveRequestedDocumentMap(
      documentId as string
    );

    // Get the index, but do not exceed the set.
    this.viewIndex = Math.min(
      Number(this.viewIndex ?? 0),
      this.requestedDocuments.size - 1
    );

    const document = this.requestedDocuments.get(
      this.viewIndex
    ) as ArchiveRequestedDocument;

    return this.searchesService
      .getDocumentData(databaseId, archiveId, document.docId, document.secureId)
      .pipe(
        tap((document) => {
          this.loadDocumentAsPdf(databaseId, archiveId, document);
        })
      );
  }

  private getSearchResultFromArchiveSession(
    sessionId: any,
    viewIndex: any,
    databaseId: number,
    archiveId: number
  ): Observable<SearchResult> {
    return this.archiveCacheQuery.selectSession(sessionId).pipe(
      map((cache) => {
        assertExists(cache);
        // Map the cached documents to the requested documents object.
        const documentIds = cache.documents.map((d) => d.id);
        this.requestedDocuments = new ArchiveSearchRequestedDocumentMap(
          documentIds
        );

        // Get the index, but do not exceed the set.
        this.viewIndex = Math.min(
          Number(viewIndex ?? 0),
          this.requestedDocuments.size - 1
        );

        // Get the document the url has requested
        const cachedDocument = cache.documents[this.viewIndex];

        assertExists(
          cachedDocument,
          'Archive document must exist in the cache to open it without a search.'
        );

        return cachedDocument;
      }),
      switchMap((cachedDocument) => {
        return this.searchesService
          .getDocumentData(
            databaseId,
            archiveId,
            cachedDocument.id,
            cachedDocument.secureId
          )
          .pipe(
            tap((document) => {
              this.loadDocumentAsPdf(databaseId, archiveId, document);
            })
          );
      })
    );
  }

  /**
   * Handle error pipelines when a document can not be loaded.
   *
   * @returns Oservable Never.*/
  private handleMissingDocument() {
    this.onLoadFailure();
    return EMPTY;
  }

  /**
   * Subscribes to the viewerAutoSave observable and adds/removes the dirtyFn.
   */
  private listenForAutoSave(): void {
    this.appQuery.viewerAutoSave$
      .pipe(untilDestroyed(this))
      .subscribe((autoSave) => {
        this.autoSave = autoSave;
      });
  }

  private listenForUserSettings = () => {
    this.appQuery.keyfreeUseOcr$.pipe(untilDestroyed(this)).subscribe((use) => {
      this.useLocalOcr = use;
    });
    this.appQuery.keyfreeAutoOcr$
      .pipe(untilDestroyed(this))
      .subscribe((auto) => {
        this.autoOcr = auto;
      });
    this.appQuery.indexerReloadAfterSave$
      .pipe(untilDestroyed(this))
      .subscribe((indexerReloadAfterSave) => {
        this.indexerReloadAfterSave = indexerReloadAfterSave;
      });
  };

  private listenToTableFieldEvent(): void {
    this.tableFieldUIService.activeTableField$.subscribe((activeTableField) => {
      this.logger.debug('Active table field or row changed.', activeTableField);
      if (this.appQuery.viewerOpenTableFieldsInSidebar) {
        this.logger.debug(
          'User has set table fields to open in the sidebar. They will not load below the document view.'
        );
        this.showTableFieldGrid = false;
        return;
      }
      this.showTableFieldGrid = !!activeTableField;
    });
  }

  private loadDocumentAsPdf(
    databaseId: number,
    archiveId: number,
    document: SearchResult
  ) {
    // Store the current open document.
    this.document = document;
    // Get a PDF for the document.
    // TODO: convert/guest bits should happen in service as part of the request to get a URL.
    const sanitizedFileExtension = document.fileType
      .replace('.', '')
      .toLowerCase();
    const isConvertableFileType =
      PdfViewerComponent.isClientConvertableExtension(
        sanitizedFileExtension,
        this.excludedClientSideFormats
      );
    const useClientConvert = isConvertableFileType;
    // If the file is not convertable or a PDF and we are a guest, then it is unsupported for this viewer.
    this.isUnsupportedDocument =
      this.auth.isGuest &&
      !isConvertableFileType &&
      sanitizedFileExtension !== 'pdf';

    // Handle unsupported document formats.
    if (this.isUnsupportedDocument) {
      this.logger.error(
        'Unsupported document format, can not be displayed in viewer.'
      );
      this.pdfOptions = undefined;
      return;
    }

    // Get a URL for the document.
    const documentUrl = this.documentProvider.getArchivePreviewUrl(
      databaseId,
      archiveId,
      document.id,
      this.auth.user.token,
      document.secureId,
      false
    );

    if (sanitizedFileExtension === 'msg' || sanitizedFileExtension === 'eml') {
      const downloadUrl = this.documentProvider.getArchiveDownloadUrl(
        databaseId,
        archiveId,
        document.id,
        this.auth.user.token,
        document.secureId
      );
      this.emailAttachmentsService
        .getAttachmentsFromEmail(sanitizedFileExtension, downloadUrl)
        .subscribe((attachments) => {
          this.documentAttachments.set(attachments);
        });
    }

    // Check for annotations.
    this.documentProvider
      .getDocumentAnnotations(
        databaseId,
        archiveId,
        this.document.id,
        this.auth.user.token,
        this.document.secureId
      )
      .subscribe((annotations) => {
        // Filter out unsupported annotations.
        let annotationsFiltered = false;
        annotations = annotations.filter((annotation) => {
          // Filter out unsupported annotation types.
          try {
            assertSupportedAnnotation(annotation);
            return true;
          } catch (_error) {
            this.logger.warn(
              'Unsupported annotation type, can not be displayed in viewer.'
            );
            annotationsFiltered = true;
            return false;
          }
        });

        // If annotations were filtered out, warn the user.
        if (annotationsFiltered) {
          this.notify.warning('UNSUPPORTED_ANNOTATIONS_FILTERED_WARNING_MSG');
        }

        this.annotationService.loadAnnotations(annotations);
        // Set the viewer options to display the requested document.
        this.pdfOptions = {
          url: useClientConvert
            ? // If convertable type, add the URL suffix.
              documentUrl +
              '&clientConvert=' +
              document.fileType.replace('.', '')
            : // Othewrwise, use the standard request URL.
              documentUrl,
          annotations,
        };
      });
  }

  private loadDocumentAsPdfFromInbox(inboxId: number, documentName: string) {
    // Get a PDF for the document.
    // The file name as it is retrieved from the API can sometimes seem to have an encoded filename
    // so ensure it is decoded before use.
    documentName = decodeURIComponent(documentName);
    // TODO: convert/guest bits should happen in service as part of the request to get a URL.
    const documentFragments = documentName.split('.');
    const sanitizedFileExtension = documentFragments[
      documentFragments.length - 1
    ]
      .replace('.', '')
      .toLowerCase();
    const isConvertableFileType =
      PdfViewerComponent.isClientConvertableExtension(
        sanitizedFileExtension,
        this.excludedClientSideFormats
      );
    const useClientConvert = isConvertableFileType;

    // TODO: Inboxes do not currently have a concept of client conversion. All files still get converted
    // at the server.

    // If the file is not convertable or a PDF and we are a guest, then it is unsupported for this viewer.
    this.isUnsupportedDocument =
      this.auth.isGuest &&
      !isConvertableFileType &&
      sanitizedFileExtension !== 'pdf';

    // Handle unsupported document formats.
    if (this.isUnsupportedDocument) {
      this.logger.error(
        'Unsupported document format, can not be displayed in viewer.'
      );
      this.pdfOptions = undefined;
      return;
    }

    const documentDownloadUrl = this.documentProvider.getInboxDownloadUrl(
      inboxId,
      documentName
    );

    // Get a URL for the document.
    let documentUrl: string;
    if (useClientConvert || sanitizedFileExtension === 'pdf') {
      documentUrl = documentDownloadUrl;

      if (sanitizedFileExtension !== 'pdf') {
        documentUrl += `&clientConvert=${sanitizedFileExtension}`;
      }
    } else {
      documentUrl = this.documentProvider.getInboxPreviewUrl(
        inboxId,
        documentName
      );

      if (
        sanitizedFileExtension === 'msg' ||
        sanitizedFileExtension === 'eml'
      ) {
        this.emailAttachmentsService
          .getAttachmentsFromEmail(sanitizedFileExtension, documentDownloadUrl)
          .subscribe((attachments) => {
            this.documentAttachments.set(attachments);
          });
      }
    }

    // Check for annotations.
    // TODO: inboxes will probably need this too...
    // this.documentProvider
    //   .getDocumentAnnotations(
    //     this.databaseId,
    //     this.archiveId,
    //     this.document.id,
    //     this.auth.user.token,
    //     this.document.secureId
    //   )
    //   .subscribe((annotations) => {
    const annotations = [] as DocumentAnnotations;
    //     // Set the viewer options to display the requested document.
    this.pdfOptions = {
      url: documentUrl,
      annotations,
      //   };
      // });
    };
  }

  private loadImportDocumentAsPdf(filename: string): void {
    const documentFragments = filename.split('.');
    const sanitizedFileExtension = documentFragments[
      documentFragments.length - 1
    ]
      .replace('.', '')
      .toLowerCase();
    const isConvertableFileType =
      PdfViewerComponent.isClientConvertableExtension(
        sanitizedFileExtension,
        this.excludedClientSideFormats
      );
    const useClientConvert = isConvertableFileType;
    // If the file is not convertable or a PDF and we are a guest, then it is unsupported for this viewer.
    this.isUnsupportedDocument =
      this.auth.isGuest &&
      !isConvertableFileType &&
      sanitizedFileExtension !== 'pdf';

    // Handle unsupported document formats.
    if (this.isUnsupportedDocument) {
      this.logger.error(
        'Unsupported document format, can not be displayed in viewer.'
      );
      this.pdfOptions = undefined;
      return;
    }

    // Get a URL for the document.
    const documentUrl = this.documentProvider.getImportFileUrl(filename);
    // const documentUrl = this.documentProvider.getInboxPreviewUrl(
    //   inboxId,
    //   documentName
    // );

    this.pdfOptions = {
      url: useClientConvert
        ? // If convertable type, add the URL suffix.
          documentUrl + '&clientConvert=' + sanitizedFileExtension
        : // Othewrwise, use the standard request URL.
          documentUrl,
      annotations: [],
      //   };
      // });
    };
  }

  /**
   * Re-navigate to the displayed document based on current routing values.
   *
   * @param forceReload Force a reload of the same route.
   * @returns An observable of whether or not navigation was successful.
   */
  private navigateToDocument(forceReload = false): Observable<boolean> {
    const navigate = (): Promise<boolean> => {
      switch (this.displayMode) {
        case 'archive': {
          return this.router.navigate(
            [
              'db',
              this.databaseId,
              'archive',
              this.archiveId,
              'search',
              this.searchId,
              'document',
              this.requestedDocuments.toString(),
              'view',
              this.viewIndex,
            ],
            {
              queryParamsHandling: 'merge',
            }
          );
        }
        case 'archiveImport':
          return this.router.navigate(
            [
              'db',
              this.databaseId,
              'archive',
              this.archiveId,
              'import',
              this.requestedDocuments.toString(),
              'view',
              this.viewIndex,
            ],
            {
              queryParamsHandling: 'merge',
            }
          );
        case 'task': {
          return this.router.navigate(
            [
              'db',
              this.databaseId,
              'archive',
              this.archiveId,
              'task',
              this.taskId,
              'document',
              this.requestedDocuments.toString(),
              'view',
              this.viewIndex,
            ],
            {
              queryParamsHandling: 'merge',
            }
          );
        }
        case 'archiveSession': {
          return this.router.navigate([
            'db',
            this.databaseId,
            'archive',
            this.archiveId,
            'document',
            'session',
            this.sessionId,
            'view',
            this.viewIndex,
          ]);
        }
        default: {
          return this.router.navigate(
            [
              'db',
              this.databaseId,
              'inbox',
              this.inboxId,
              'document',
              this.requestedDocuments.toString(),
              'view',
              this.viewIndex,
            ],
            {
              queryParamsHandling: 'merge',
            }
          );
        }
      }
    };

    if (forceReload) {
      // Trip router to cause a reuse.
      return from(
        this.router.navigateByUrl('/', {
          skipLocationChange: true,
          state: { reloading: true },
        })
      ).pipe(switchMap(() => from(navigate())));
    } else {
      return from(navigate());
    }
  }

  /**
   * Run the specified search and load the result data, of one document.
   *
   * @param archiveId Archive Id.
   * @param searchId Search Id.
   * @param searchPrompts Search parameter data.
   * @param tabId Tab Id to restrict the search to. 0 will not filter.
   * @param documentId Document Id.
   * @returns Observable search result.
   */
  private runSearchForSingleDocument(
    archiveId: number,
    searchId: number,
    searchPrompts: SearchPrompt[],
    tabId: number = 0,
    documentId: number
  ) {
    this.isSearchLoading = true;
    const defaultSearchOptions: SearchOptions = {
      page: 1,
      countOnly: false,
      recordsPerPage: 1,
      searchCriteria: searchPrompts
        ? createApiSearchPromptString(searchPrompts)
        : '',
      sort: '',
      tabId,
      targetArchiveId: archiveId,
      documentId,
      includeExtendedData: true,
    };

    // Use a select for the search, ignoring nil values.
    // This will make the actual run of the search wait for the search to be loaded.
    // Otherwise this can cause issues when loading directly from a shared link to an instance.
    const search$ = this.searchesQuery.isLoading$.pipe(
      untilDestroyed(this),
      first((isLoading) => isLoading === false),
      map(() => this.searchesQuery.getEntity(searchId)),
      tap((search) => {
        if (!search) {
          // We don't know for sure why the search wasn't found in the store
          // but it was probably because the user is not secured.
          const error = new UserFriendlyError(
            undefined,
            'Search was not found in the entity store. This is likely because the user does not have permissions to the search.',
            'SEARCH_NOT_AVAILABLE'
          );
          this.notify.error(error);
          this.router.navigate(['db', this.databaseId], {
            queryParamsHandling: 'merge',
          });
        }
      }),
      filterNilValue()
    );
    // Execute the search.
    return search$.pipe(
      switchMap((search) =>
        this.searchesService.run(search, defaultSearchOptions).pipe(
          untilDestroyed(this),
          map((resultResponse) => {
            this.logger.debug('Search results:', resultResponse);
            // Get a single result.
            const matchedDocument = resultResponse.searchResults[0];
            assertExists(
              matchedDocument,
              'No document was found matching query.'
            );
            return matchedDocument;
          }),
          tap(() => (this.isSearchLoading = false))
        )
      )
    );
  }
}
